// ============================================================
// market.jsx — Standverkauf-Modus (type='market' Stores)
// ES-Module: importiert von theme, modal-utils, widgets, sync, store-sheets.
//
// Komponenten:
//   MarketHub                — Top-Level: Tab-Layout ODER aktive Session
//   MarketSessionsList       — Tab "Markt-Tage"
//   MarketInventoryList      — Tab "Sortiment"
//   MarketStats              — Tab "Statistik"
//   MarketActiveSession      — Hauptbildschirm während Markt läuft
//   MarketItemEditSheet      — Produkt anlegen/bearbeiten
//   MarketRestockSheet       — Lager auffüllen
//   MarketSessionStartSheet  — Markt-Tag setup (Ort + Sortiment-Auswahl)
//   MarketSaleSheet          — Einzel-Verkauf mit Mengen-Stepper + Rabatt
//   MarketBundleSheet        — Bundle-Verkauf mit Mengen
//   MarketSaleStornoSheet    — Verkauf stornieren
//   MarketSessionEndSheet    — Tagesabschluss (Top-Produkte, Lager-Diff)
//   MarketSessionDetail      — Detail-Ansicht vergangener Markt-Tag
//   MarketActiveSheet        — Modal "Markt läuft schon"
//   MarketAddNoteSheet       — Notiz mid-Session
//   ConfirmSheet             — generische Bestätigung (shared, aus widgets.jsx)
//
// Datenmodell-Konvention für Standverkauf (vs. Trödel):
//   items.price       — fester Verkaufspreis (Pflichtfeld in UI)
//   items.quantity    — echter Lagerbestand (Integer, Pflichtfeld)
//   items.wish_price  — bleibt NULL (Standverkauf hat keinen Wunschpreis)
//   sale_items.quantity   — Mehrfach-Stück möglich
//   sale_items.unit_price — Standard-Einzelpreis (vor Rabatt)
//   sale_items.wish_price — bleibt NULL für Einzelverkäufe;
//                           bei Bundles = Standard-Preis pro Stück (für Stat)
// ============================================================
import { useState as useStateM, useEffect as useEffectM, useRef as useRefM, useMemo as useMemoM } from 'react';
import { F, T, Icon, ACCENTS, haptic } from './theme.jsx';
import { useModalViewport, modalShellStyle, ModalCloseButton, ModalHeader, bottomSheetShellStyle, BottomSheetHeader } from './modal-utils.jsx';
import { BottomSheet, useValidation, ConfirmSheet, ActionMenu, fmtCurrency, apiCall, computeBaseStats, SwipeToDelete } from './widgets.jsx';
import { SyncIndicator, syncManager, usePWAInstall } from './sync.jsx';
import { ConfirmDeleteStoreSheet, ModeMembersSheet, SettingsSectionLabel, SettingsCard, SettingsRow, exportSingleStoreCsv } from './store-sheets.jsx';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const fmtDateM = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";
const fmtTimeM = (iso) => iso ? new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "";
const fmtMonthM = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : "";
const fmtSinceM = (iso) => {
  if (!iso) return "";
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "gerade gestartet";
  if (min < 60) return `seit ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `seit ${h} Std`;
  return `seit ${Math.floor(h / 24)} Tag${Math.floor(h / 24) === 1 ? "" : "en"}`;
};
const fmtDurationM = (start, end) => {
  if (!start) return "";
  const ms = (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), rest = min % 60;
  return rest === 0 ? `${h} Std` : `${h} Std ${rest} min`;
};

// Live-Time-Diff in Minuten seit gegebenem ISO
const minutesSinceM = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
const fmtAgoM = (iso) => {
  const m = minutesSinceM(iso);
  if (m < 1) return "gerade eben";
  if (m < 60) return `vor ${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `vor ${h} Std` : `vor ${Math.floor(h / 24)} Tg`;
};

// Statistik aus Sales-Array (Standverkauf-Semantik: Rabatt = sum(qty*unit_price) − total)
function computeMarketStats(sales) {
  const base = computeBaseStats(sales);
  // Standard-Summe = sum(qty * unit_price) für Einzel-Sales,
  //                  sum(qty * wish_price) für Bundle-Sales (wish_price = Standard-Preis bei Bundles)
  let standard = 0, totalDiscount = 0, discountedSales = 0;
  for (const s of base.active) {
    const items = s.sale_items || [];
    const std = items.reduce((sum, i) => {
      const ref = s.is_bundle ? Number(i.wish_price) : Number(i.unit_price);
      return sum + (Number.isFinite(ref) ? ref * Number(i.quantity || 1) : 0);
    }, 0);
    standard += std;
    if (std > 0 && Number(s.total) < std) { totalDiscount += (std - Number(s.total)); discountedSales++; }
  }
  return {
    ...base,
    standard, totalDiscount, discountedSales,
    avgDiscountPct: standard > 0 ? Math.round((totalDiscount / standard) * 100) : 0,
  };
}

// ─────────────────────────────────────────────────────────────
// MarketHub — Top-Level. Lädt items, sessions, activeSession parallel.
// Wenn activeSession existiert: render MarketActiveSession (außer User
// hat showHubDuringSession aktiviert).
// ─────────────────────────────────────────────────────────────
function MarketHub({ store, authToken, onBack, onStoreDeleted, dark, showToast }) {
  const t = T[dark ? "dark" : "light"];
  const [loading, setLoading] = useStateM(true);
  const [items, setItems] = useStateM([]);
  const [hasMoreItems, setHasMoreItems] = useStateM(false);
  const [itemsCursor, setItemsCursor] = useStateM(null);
  const [sessions, setSessions] = useStateM([]);
  const [hasMoreSessions, setHasMoreSessions] = useStateM(false);
  const [sessionsCursor, setSessionsCursor] = useStateM(null);
  const [activeSession, setActiveSession] = useStateM(null);
  const [tab, setTab] = useStateM("sessions"); // 'sessions' | 'inventory' | 'stats'
  const [viewingSessionId, setViewingSessionId] = useStateM(null);
  const [showHubDuringSession, setShowHubDuringSession] = useStateM(false);
  const [settingsOpen, setSettingsOpen] = useStateM(false);
  const [error, setError] = useStateM(null);
  // P-5-Punkte-Fix Block 5: Pull-to-Refresh
  const [refreshing, setRefreshing] = useStateM(false);
  const ptrStartY = useRefM(null);
  const [ptrDistance, setPtrDistance] = useStateM(0);

  const apiBase = `/api/stores/${store.id}`;

  const reloadAll = async () => {
    setLoading(true); setError(null);
    try {
      const [itemsR, sessionsR, activeR] = await Promise.all([
        apiCall(authToken, `${apiBase}/items`),
        apiCall(authToken, `${apiBase}/sessions`),
        apiCall(authToken, `${apiBase}/sessions/active`),
      ]);
      // Sessions + Items kommen jetzt paginiert: { sessions/items, has_more, next_cursor }
      const itemsArr = Array.isArray(itemsR) ? itemsR : (itemsR?.items || []);
      setItems(itemsArr);
      setHasMoreItems(!!itemsR?.has_more);
      setItemsCursor(itemsR?.next_cursor || null);

      const sessionsArr = Array.isArray(sessionsR) ? sessionsR : (sessionsR?.sessions || []);
      setSessions(sessionsArr);
      setHasMoreSessions(!!sessionsR?.has_more);
      setSessionsCursor(sessionsR?.next_cursor || null);

      setActiveSession(activeR);
    } catch (e) {
      console.warn("MarketHub reload failed:", e);
      setError(e.message || "Lade-Fehler");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreSessions = async () => {
    if (!sessionsCursor) return;
    try {
      const r = await apiCall(authToken, `${apiBase}/sessions?before=${encodeURIComponent(sessionsCursor)}`);
      const arr = Array.isArray(r) ? r : (r?.sessions || []);
      setSessions(prev => [...prev, ...arr]);
      setHasMoreSessions(!!r?.has_more);
      setSessionsCursor(r?.next_cursor || null);
    } catch (e) { console.warn("loadMoreSessions failed:", e); }
  };

  const loadMoreItems = async () => {
    if (!itemsCursor) return;
    try {
      const r = await apiCall(authToken, `${apiBase}/items?before=${encodeURIComponent(itemsCursor)}`);
      const arr = Array.isArray(r) ? r : (r?.items || []);
      setItems(prev => [...prev, ...arr]);
      setHasMoreItems(!!r?.has_more);
      setItemsCursor(r?.next_cursor || null);
    } catch (e) { console.warn("loadMoreItems failed:", e); }
  };

  useEffectM(() => { reloadAll(); /* eslint-disable-next-line */ }, [store?.id]);

  // Body-Scroll-Lock + reset scroll position so header is always visible
  useEffectM(() => {
    window.scrollTo(0, 0);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Aktive Session vorhanden? → MarketActiveSession render
  if (activeSession && !viewingSessionId && !showHubDuringSession) {
    return (
      <MarketActiveSession
        store={store} authToken={authToken} dark={dark} t={t}
        session={activeSession}
        items={items}
        onSessionChanged={(updated) => {
          if (updated.ends_at) {
            setActiveSession(null);
            setShowHubDuringSession(false);
            reloadAll();
          } else {
            setActiveSession(updated);
          }
        }}
        onItemsChanged={(newItems) => setItems(newItems)}
        showToast={showToast}
        onBackToHub={() => setShowHubDuringSession(true)}
      />
    );
  }

  // Detail-View einer vergangenen Session
  if (viewingSessionId) {
    return (
      <MarketSessionDetail
        store={store} authToken={authToken} dark={dark} t={t}
        sessionId={viewingSessionId}
        items={items}
        showToast={showToast}
        onBack={() => setViewingSessionId(null)}
        onSessionChanged={() => reloadAll()}
      />
    );
  }

  // Hub-Tab-Layout
  const bg = dark ? "#0d1117" : "#dde8f8";
  return (
    <div style={{
      height: "100dvh", maxHeight: "100dvh",
      background: bg, fontFamily: F, color: t.text,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Sticky Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)",
        paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
        background: dark ? "rgba(13,17,23,0.85)" : "rgba(221,232,248,0.85)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: `0.5px solid ${t.glassBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <button onClick={onBack} aria-label="Zurück" style={{
            width: 30, height: 30, borderRadius: 14, flexShrink: 0,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: t.text, letterSpacing: "-0.4px" }}>Standverkauf</div>
            <div style={{ fontSize: 12, color: t.textSub, marginTop: 1 }}>{store.name}</div>
          </div>
          <button onClick={() => { setSettingsOpen(true); haptic(8); }} aria-label="Einstellungen" style={{
            width: 30, height: 30, borderRadius: 15, flexShrink: 0,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
        </div>

        {/* Live-Banner: Markt läuft → tap to return */}
        {activeSession && showHubDuringSession && (
          <button onClick={() => { setShowHubDuringSession(false); haptic(8); }} style={{
            width: "100%", marginBottom: 12, padding: "10px 12px",
            background: "linear-gradient(180deg, #30D158 0%, #1BAE43 100%)",
            border: "none", borderRadius: 12,
            color: "#fff", fontFamily: F, fontWeight: 600, fontSize: 13,
            cursor: "pointer", textAlign: "left",
            display: "flex", alignItems: "center", gap: 10,
            boxShadow: "0 4px 14px rgba(48,209,88,0.35)",
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%", background: "#fff",
              animation: "pulse 1.6s ease-in-out infinite", flexShrink: 0,
            }}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, letterSpacing: "0.04em" }}>
                MARKT LÄUFT · {activeSession.location || "Standverkauf"}
              </div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 1 }}>
                Tippen um zurückzukehren · {fmtSinceM(activeSession.starts_at)}
              </div>
            </div>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
              <path d="M5 2L11 7L5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Tab-Bar */}
        <div style={{
          background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
          borderRadius: 12, padding: 3,
          display: "flex", gap: 4,
        }}>
          {[
            { id: "sessions",  label: `Markt-Tage${sessions.length ? ` · ${sessions.length}` : ""}` },
            { id: "inventory", label: `Sortiment${items.filter(i => i.active !== false).length ? ` · ${items.filter(i => i.active !== false).length}` : ""}` },
            { id: "stats",     label: "Statistik" },
          ].map(tb => {
            const active = tab === tb.id;
            return (
              <button key={tb.id} onClick={() => { setTab(tb.id); haptic(6); }} style={{
                flex: 1, padding: "8px 6px", border: "none", cursor: "pointer", fontFamily: F,
                fontSize: 13, fontWeight: active ? 700 : 500, borderRadius: 9,
                background: active ? (dark ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.95)") : "transparent",
                color: active ? t.text : t.textSub,
                boxShadow: active ? `0 1px 4px rgba(0,0,0,${dark ? 0.3 : 0.08})` : "none",
                transition: "all 0.16s ease",
              }}>{tb.label}</button>
            );
          })}
        </div>
      </div>

      {/* Tab-Content (mit Pull-to-Refresh — P-5-Punkte-Fix Block 5) */}
      <div
        style={{
          flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch",
          padding: "16px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)",
          transform: ptrDistance > 0 ? `translateY(${Math.min(ptrDistance, 90)}px)` : "translateY(0)",
          transition: ptrDistance === 0 ? "transform 0.2s cubic-bezier(0.34,1.2,0.64,1)" : "none",
        }}
        onTouchStart={(e) => {
          if (e.currentTarget.scrollTop <= 0) ptrStartY.current = e.touches[0].clientY;
        }}
        onTouchMove={(e) => {
          if (ptrStartY.current === null) return;
          const dy = e.touches[0].clientY - ptrStartY.current;
          if (dy > 0 && e.currentTarget.scrollTop <= 0) setPtrDistance(Math.min(dy, 120));
          else if (dy < 0) { ptrStartY.current = null; setPtrDistance(0); }
        }}
        onTouchEnd={async () => {
          if (ptrDistance >= 70 && !refreshing) {
            setRefreshing(true);
            try { await reloadAll(); } finally { setRefreshing(false); }
            haptic(15);
          }
          setPtrDistance(0);
          ptrStartY.current = null;
        }}
      >
        {(ptrDistance > 0 || refreshing) && (
          <div style={{
            position: "absolute", top: 0, left: "50%", transform: `translateX(-50%) translateY(${Math.min(ptrDistance/2, 30)}px)`,
            color: t.textMuted, fontSize: 14, zIndex: 1,
          }}>
            {refreshing ? "↻" : ptrDistance >= 70 ? "↑ Loslassen" : "↓ Ziehen"}
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: "center", color: t.textMuted, padding: 40, fontSize: 14 }}>Lade…</div>
        ) : error ? (
          <div style={{ textAlign: "center", color: t.accent.red.solid, padding: 40, fontSize: 14 }}>
            {error}
            <div style={{ marginTop: 12 }}>
              <button onClick={reloadAll} style={{
                background: t.accent.blue.solid, color: "#fff", border: "none",
                borderRadius: 10, padding: "8px 16px", cursor: "pointer", fontFamily: F, fontWeight: 600, fontSize: 13,
              }}>Neu laden</button>
            </div>
          </div>
        ) : tab === "sessions" ? (
          <>
            <MarketSessionsList
              store={store} authToken={authToken} dark={dark} t={t}
              sessions={sessions}
              items={items}
              activeSession={activeSession}
              onSessionStarted={(newSession) => {
                setActiveSession(newSession);
                setSessions([newSession, ...sessions]);
                setShowHubDuringSession(false);
              }}
              onSessionDeleted={(sid) => {
                setSessions(prev => prev.filter(x => x.id !== sid));
              }}
              onResumeActive={() => setShowHubDuringSession(false)}
              onOpenSession={(sid) => setViewingSessionId(sid)}
              showToast={showToast}
            />
            {hasMoreSessions && (
              <div style={{ textAlign: "center", padding: "14px 0 4px" }}>
                <button onClick={loadMoreSessions} style={{
                  background: "none", border: `1px solid ${t.glassBorder}`,
                  borderRadius: 10, padding: "8px 20px", cursor: "pointer",
                  color: t.textMuted, fontFamily: F, fontSize: 13, fontWeight: 600,
                }}>Mehr laden</button>
              </div>
            )}
          </>
        ) : tab === "inventory" ? (
          <>
            <MarketInventoryList
              store={store} authToken={authToken} dark={dark} t={t}
              items={items}
              onItemsChanged={(newItems) => setItems(newItems)}
              showToast={showToast}
            />
            {hasMoreItems && (
              <div style={{ textAlign: "center", padding: "14px 0 4px" }}>
                <button onClick={loadMoreItems} style={{
                  background: "none", border: `1px solid ${t.glassBorder}`,
                  borderRadius: 10, padding: "8px 20px", cursor: "pointer",
                  color: t.textMuted, fontFamily: F, fontSize: 13, fontWeight: 600,
                }}>Mehr laden</button>
              </div>
            )}
          </>
        ) : (
          <MarketStats
            sessions={sessions}
            items={items}
            authToken={authToken}
            store={store}
            dark={dark} t={t}
          />
        )}
      </div>

      <MarketSettings
        isOpen={settingsOpen}
        store={store} authToken={authToken} dark={dark} t={t}
        sessionsCount={sessions.length}
        itemsCount={items.length}
        onClose={() => setSettingsOpen(false)}
        onStoreDeleted={onStoreDeleted || onBack}
        showToast={showToast}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketSettings — Standverkauf-Einstellungen (Stub mit funktionalen Aktionen)
// Inhalte/Sektionen folgen einem späteren Prompt analog zu FleaSettings.
// Hier bereits funktional: Export, Bereich löschen.
// ─────────────────────────────────────────────────────────────
function MarketSettings({ isOpen, store, authToken, dark, t, sessionsCount, itemsCount, onClose, onStoreDeleted, showToast }) {
  const [exporting, setExporting] = useStateM(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useStateM(false);
  // P-5-Punkte-Fix Block 5: Mitglieder-Verwaltung
  const [membersPanelOpen, setMembersPanelOpen] = useStateM(false);

  const BS  = BottomSheet;
  const SLb = SettingsSectionLabel;
  const SCa = SettingsCard;
  const SRo = SettingsRow;

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportSingleStoreCsv(store, authToken);
      showToast("Export heruntergeladen");
    } catch (e) {
      showToast(e.message || "Export fehlgeschlagen", "error");
    } finally {
      setExporting(false);
    }
  };

  const handleDestroyStore = async () => {
    try {
      const res = await fetch(`/api/stores/${store.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        showToast(d.reason || "Löschen fehlgeschlagen", "error");
        return;
      }
      showToast("Standverkauf-Bereich gelöscht");
      onStoreDeleted();
    } catch (e) {
      showToast("Netzwerkfehler", "error");
    }
  };


  return (
    <>
      <BS isOpen={isOpen} onClose={onClose} title="Standverkauf-Einstellungen" subtitle={store.name} dark={dark} t={t} F={F}>
        <SLb t={t}>Sortiment</SLb>
        <SCa dark={dark} t={t}>
          <SRo isFirst label="Produkte" value={String(itemsCount)} t={t} dark={dark} F={F} />
          <SRo label="Markt-Tage" value={String(sessionsCount)} t={t} dark={dark} F={F} />
        </SCa>

        <SLb t={t}>Anzeige</SLb>
        <SCa dark={dark} t={t}>
          <SRo isFirst label="Inhalte folgen" sub="Helfer-Modus & Filter kommen in einem späteren Prompt" t={t} dark={dark} F={F} />
        </SCa>

        {/* Mitglieder-Verwaltung (P-5-Punkte-Fix Block 5) */}
        <SLb t={t}>Mitglieder</SLb>
        <SCa dark={dark} t={t}>
          <SRo
            isFirst
            label="Mitglieder verwalten"
            sub="Code generieren · Mitglieder entfernen"
            onClick={() => setMembersPanelOpen(true)}
            t={t} dark={dark} F={F}
          />
        </SCa>

        <SLb t={t}>Daten</SLb>
        <SCa dark={dark} t={t}>
          <SRo
            isFirst
            label="Standverkauf-Daten exportieren"
            sub={exporting ? "Exportiere…" : "ZIP mit Sortiment, Sessions und Verkäufen"}
            onClick={!exporting ? handleExport : undefined}
            t={t} dark={dark} F={F}
          />
        </SCa>

        <SLb t={t}>Bereich</SLb>
        <SCa dark={dark} t={t}>
          <SRo
            isFirst
            label="Standverkauf-Bereich löschen"
            sub="Alle Daten werden permanent gelöscht"
            destructive
            onClick={() => setConfirmDeleteOpen(true)}
            t={t} dark={dark} F={F}
          />
        </SCa>
      </BS>

      {confirmDeleteOpen && (
        <ConfirmDeleteStoreSheet
          dark={dark} t={t}
          storeName={store.name}
          storeType="market"
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={async () => { await handleDestroyStore(); setConfirmDeleteOpen(false); }}
        />
      )}

      {/* Mitglieder-Sheet (P-5-Punkte-Fix Block 5) */}
      <ModeMembersSheet
        isOpen={membersPanelOpen}
        store={store} authToken={authToken} dark={dark} t={t} showToast={showToast}
        onClose={() => setMembersPanelOpen(false)}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketSessionsList — Tab "Markt-Tage"
// ─────────────────────────────────────────────────────────────
function MarketSessionsList({ store, authToken, dark, t, sessions, items, activeSession, onSessionStarted, onSessionDeleted, onResumeActive, onOpenSession, showToast }) {
  const [startSheetOpen, setStartSheetOpen] = useStateM(false);
  const [activeWarnOpen, setActiveWarnOpen] = useStateM(false);
  // P-Swipe-Delete: ein Card zur Zeit
  const swipeTracker = useRefM({ activeId: null, registry: {} });

  // Hard-Delete eines Markt-Tags (CASCADE im Worker, Lager-Restore inklusive)
  const deleteSession = async (sessionId) => {
    const res = await fetch(`/api/stores/${store.id}/sessions/${sessionId}`, {
      method: "DELETE",
      headers: { "Authorization": `Bearer ${authToken}` },
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.reason || "Markt-Tag konnte nicht gelöscht werden");
    }
    if (onSessionDeleted) onSessionDeleted(sessionId);
  };

  // Pro-Session basic stats für Confirm-Dialog. Bei Markt haben wir keine
  // embedded sales — Approximation: wenn ends_at existiert, war's ein
  // abgeschlossener Markt-Tag (der Caller liefert keine sales-Counts; wir
  // können nichts genaues sagen). Wir zeigen "Verkäufe werden permanent
  // gelöscht" generisch.
  const swipeConfirmSubtitle = (s) =>
    s.ends_at ? "Alle Verkäufe dieses Markt-Tags werden permanent gelöscht."
              : "Der Markt-Tag wird permanent gelöscht.";

  // Per-Session Stats (basic count/total) brauchen Sale-Daten — die haben wir
  // hier nicht batched. Wie bei Trödel: List zeigt nur Datum+Ort, Detail-View
  // lädt Sales nach.
  const grouped = useMemoM(() => {
    const byMonth = new Map();
    for (const s of sessions) {
      const key = fmtMonthM(s.starts_at);
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key).push(s);
    }
    return [...byMonth.entries()];
  }, [sessions]);

  const handleStartTap = () => {
    haptic(10);
    if (activeSession) {
      setActiveWarnOpen(true);
    } else {
      setStartSheetOpen(true);
    }
  };

  return (
    <>
      <button onClick={handleStartTap} style={{
        width: "100%", padding: "16px",
        background: "linear-gradient(180deg, #30D158 0%, #1BAE43 100%)",
        border: "none", borderRadius: 14,
        color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 16, letterSpacing: "-0.2px",
        cursor: "pointer", marginBottom: 18,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        boxShadow: "0 6px 22px rgba(48,209,88,0.4)",
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        Neuer Markt-Tag
      </button>

      {sessions.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: t.textSub }}>
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>
            Noch kein Markt-Tag.<br/>Tipp oben auf „Neuer Markt-Tag" um zu starten.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {grouped.map(([month, list]) => (
            <div key={month}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: t.textMuted,
                letterSpacing: "0.08em", textTransform: "uppercase",
                marginBottom: 8, paddingLeft: 4,
              }}>{month}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {list.map(s => {
                  const isLive = !s.ends_at;
                  const cardContent = (
                    <button onClick={() => {
                      if (isLive && activeSession?.id === s.id) {
                        onResumeActive();
                      } else {
                        onOpenSession(s.id);
                      }
                    }} style={{
                      width: "100%",
                      background: dark ? "rgba(28,28,30,0.95)" : "rgba(255,255,255,0.95)",
                      border: `1px solid ${t.glassBorder}`,
                      borderRadius: 14, padding: "14px 16px",
                      cursor: "pointer", fontFamily: F, textAlign: "left",
                      display: "flex", alignItems: "center", gap: 12,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: t.text }}>
                            {fmtDateM(s.starts_at)} · {fmtTimeM(s.starts_at)}
                          </div>
                          {isLive && (
                            <div style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              fontSize: 10, fontWeight: 700, color: t.accent.green.solid,
                              background: t.accent.green.weak,
                              padding: "2px 7px", borderRadius: 5,
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.accent.green.solid, animation: "pulse 1.6s ease-in-out infinite" }}/>
                              LIVE
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: t.textSub }}>
                          {s.location || "Ohne Ort"}
                          {isLive && ` · ${fmtSinceM(s.starts_at)}`}
                          {!isLive && ` · Dauer ${fmtDurationM(s.starts_at, s.ends_at)}`}
                        </div>
                      </div>
                      <div style={{ fontSize: 22, color: t.chevron, fontWeight: 200, flexShrink: 0 }}>›</div>
                    </button>
                  );

                  return (
                    <SwipeToDelete
                      key={s.id}
                      id={`market-session-${s.id}`}
                      tracker={swipeTracker}
                      threshold={80}
                      disabled={isLive}
                      disabledMessage="Markt läuft. Bitte erst beenden."
                      confirmTitle={`Markt-Tag vom ${fmtDateM(s.starts_at)}${s.location ? ` · ${s.location}` : ""} wirklich löschen?`}
                      confirmSubtitle={swipeConfirmSubtitle(s)}
                      confirmWarning="Diese Aktion kann nicht rückgängig gemacht werden."
                      onConfirm={async () => { await deleteSession(s.id); }}
                      showToast={showToast}
                      t={t} dark={dark} F={F}
                    >
                      {cardContent}
                    </SwipeToDelete>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {startSheetOpen && (
        <MarketSessionStartSheet
          store={store} authToken={authToken} dark={dark} t={t}
          items={items}
          lastSession={sessions.find(s => s.ends_at) || sessions[0]}
          recentLocations={[...new Set(sessions.slice(0, 30).map(s => s.location).filter(Boolean))].slice(0, 5)}
          onClose={() => setStartSheetOpen(false)}
          onStarted={(newSession) => {
            setStartSheetOpen(false);
            onSessionStarted(newSession);
          }}
          showToast={showToast}
        />
      )}

      {activeWarnOpen && activeSession && (
        <MarketActiveSheet
          dark={dark} t={t}
          session={activeSession}
          onCancel={() => setActiveWarnOpen(false)}
          onResume={() => { setActiveWarnOpen(false); onResumeActive(); }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketActiveSheet — Modal "Markt läuft schon"
// ─────────────────────────────────────────────────────────────
function MarketActiveSheet({ dark, t, session, onCancel, onResume }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  return (
    <>
      <div onClick={onCancel} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 360, radius: 24, padding: "24px 24px 22px", zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Markt läuft bereits" onClose={onCancel} marginBottom={12} />
        <div style={{ fontSize: 14, color: t.textSub, lineHeight: 1.5, marginBottom: 18 }}>
          {session.location ? <><b style={{ color: t.text }}>{session.location}</b>, </> : null}
          {fmtSinceM(session.starts_at)}.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={onResume} style={{
            width: "100%", padding: "13px",
            background: t.accent.green.solid, color: "#fff",
            border: "none", borderRadius: 12,
            fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: "pointer",
          }}>Aktuellen Markt fortsetzen</button>
          <button onClick={onCancel} style={{
            width: "100%", padding: "11px",
            background: "transparent", border: "none",
            color: t.textMuted,
            fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
          }}>Abbrechen</button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketInventoryList — Tab "Sortiment"
// ─────────────────────────────────────────────────────────────
function MarketInventoryList({ store, authToken, dark, t, items, onItemsChanged, showToast }) {
  const [editing, setEditing] = useStateM(null); // null | item | "new"
  const [restocking, setRestocking] = useStateM(null); // null | item
  const [confirmDelete, setConfirmDelete] = useStateM(null);

  // Nur aktive Items, alphabetisch
  const sortedItems = useMemoM(() =>
    items.filter(it => it.active !== false).sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"))
  , [items]);

  // Footer-Stats
  const footer = useMemoM(() => {
    const active = items.filter(it => it.active !== false);
    const stockValue = active.reduce((s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 0), 0);
    const outOfStock = active.filter(it => (Number(it.quantity) || 0) === 0).length;
    const lowStock = active.filter(it => { const q = Number(it.quantity) || 0; return q > 0 && q < 5; }).length;
    return { count: active.length, stockValue, outOfStock, lowStock };
  }, [items]);

  const persistItem = async (data, idForUpdate) => {
    const apiBase = `/api/stores/${store.id}`;
    try {
      if (idForUpdate) {
        const updated = await apiCall(authToken, `${apiBase}/items/${idForUpdate}`, { method: "PATCH", body: data });
        onItemsChanged(items.map(it => it.id === idForUpdate ? updated : it));
        showToast("Produkt aktualisiert");
      } else {
        const created = await apiCall(authToken, `${apiBase}/items`, { method: "POST", body: data });
        onItemsChanged([created, ...items]);
        showToast("Produkt angelegt");
      }
      haptic(15);
    } catch (e) {
      showToast(e.message || "Speichern fehlgeschlagen", "error");
    }
  };

  const restock = async (itemId, addQty, note) => {
    const apiBase = `/api/stores/${store.id}`;
    try {
      const target = items.find(it => it.id === itemId);
      const newQ = (Number(target?.quantity) || 0) + Number(addQty);
      const body = { quantity: newQ };
      // Notiz wird im audit_log mitgeschrieben (Worker schreibt das automatisch)
      // — wir packen sie zusätzlich in metadata.last_restock_note
      if (note) body.metadata = { ...(target?.metadata || {}), last_restock_note: note, last_restock_at: new Date().toISOString() };
      const updated = await apiCall(authToken, `${apiBase}/items/${itemId}`, { method: "PATCH", body });
      onItemsChanged(items.map(it => it.id === itemId ? updated : it));
      showToast(`+${addQty} aufgefüllt`);
      haptic(15);
    } catch (e) {
      showToast(e.message || "Auffüllen fehlgeschlagen", "error");
    }
  };

  const softDeleteItem = async (id) => {
    const apiBase = `/api/stores/${store.id}`;
    try {
      await apiCall(authToken, `${apiBase}/items/${id}`, { method: "DELETE" });
      onItemsChanged(items.filter(it => it.id !== id));
      showToast("Produkt entfernt");
      haptic(15);
    } catch (e) {
      showToast(e.message || "Löschen fehlgeschlagen", "error");
    }
  };

  return (
    <>
      <button onClick={() => { setEditing("new"); haptic(10); }} style={{
        width: "100%", padding: "14px",
        background: t.accent.blue.solid,
        border: "none", borderRadius: 14,
        color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 15,
        cursor: "pointer", marginBottom: 16,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        boxShadow: `0 4px 18px ${t.accent.blue.weak}`,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        Neues Produkt
      </button>

      {sortedItems.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 20px", color: t.textSub, fontSize: 14, lineHeight: 1.5 }}>
          Noch keine Produkte im Sortiment.<br/>Tipp oben auf „Neues Produkt".
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {sortedItems.map(it => {
              const q = Number(it.quantity) || 0;
              const out = q === 0;
              const low = !out && q < 5;
              return (
                <div key={it.id} style={{
                  background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
                  border: `1px solid ${out ? t.accent.red.solid + "40" : low ? t.accent.orange.solid + "40" : t.glassBorder}`,
                  borderRadius: 12,
                  display: "flex", alignItems: "stretch",
                  opacity: out ? 0.7 : 1,
                }}>
                  <button onClick={() => setEditing(it)} style={{
                    flex: 1, minWidth: 0,
                    background: "transparent", border: "none",
                    padding: "12px 14px",
                    cursor: "pointer", fontFamily: F, textAlign: "left",
                    display: "flex", alignItems: "center", gap: 12,
                  }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                      background: out ? t.accent.red.weak : low ? t.accent.orange.weak : t.accent.green.weak,
                      color: out ? t.accent.red.solid : low ? t.accent.orange.solid : t.accent.green.solid,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                    }}>
                      {q}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>{it.name}</div>
                        {out && (
                          <span style={{ fontSize: 10, fontWeight: 700, background: t.accent.red.weak, color: t.accent.red.solid, padding: "2px 7px", borderRadius: 5, flexShrink: 0 }}>AUSVERKAUFT</span>
                        )}
                        {low && !out && (
                          <span style={{ fontSize: 10, fontWeight: 700, background: t.accent.orange.weak, color: t.accent.orange.solid, padding: "2px 7px", borderRadius: 5, flexShrink: 0 }}>WENIG</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: t.textSub, marginTop: 1, fontVariantNumeric: "tabular-nums" }}>
                        {fmtCurrency(it.price)} · {q} {q === 1 ? "Stück" : "Stück"} im Lager
                      </div>
                    </div>
                  </button>
                  <button onClick={() => { setRestocking(it); haptic(8); }} aria-label="Auffüllen" style={{
                    background: "transparent",
                    borderLeft: `0.5px solid ${t.divider}`,
                    border: "none",
                    width: 50, flexShrink: 0,
                    cursor: "pointer", color: t.accent.blue.solid,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Footer-Stats */}
          <div style={{
            marginTop: 18, padding: "14px 16px",
            background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
            borderRadius: 12,
            fontSize: 12, color: t.textSub,
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
            flexWrap: "wrap",
          }}>
            <div>
              <span style={{ color: t.text, fontWeight: 600 }}>{footer.count}</span> {footer.count === 1 ? "Produkt" : "Produkte"}
              {footer.outOfStock > 0 && <span style={{ marginLeft: 8, color: t.accent.red.solid, fontWeight: 600 }}>· {footer.outOfStock} ausverkauft</span>}
              {footer.lowStock > 0 && <span style={{ marginLeft: 8, color: t.accent.orange.solid, fontWeight: 600 }}>· {footer.lowStock} wenig</span>}
            </div>
            <div style={{ fontVariantNumeric: "tabular-nums" }}>
              Lagerwert <span style={{ color: t.text, fontWeight: 700 }}>{fmtCurrency(footer.stockValue)}</span>
            </div>
          </div>
        </>
      )}

      {editing && (
        <MarketItemEditSheet
          item={editing === "new" ? null : editing}
          dark={dark} t={t}
          onClose={() => setEditing(null)}
          onSave={async (data) => { await persistItem(data, editing === "new" ? null : editing.id); setEditing(null); }}
          onDelete={editing !== "new" ? () => { setConfirmDelete(editing); } : null}
        />
      )}

      {restocking && (
        <MarketRestockSheet
          item={restocking}
          dark={dark} t={t}
          onClose={() => setRestocking(null)}
          onConfirm={async (addQty, note) => {
            await restock(restocking.id, addQty, note);
            setRestocking(null);
          }}
        />
      )}

      {confirmDelete && (
        <ConfirmSheet
          dark={dark} t={t}
          title="Produkt entfernen?"
          subtitle={confirmDelete.name}
          warning="Das Produkt wird inaktiv gesetzt. Bisherige Verkäufe bleiben in der Historie erhalten."
          confirmLabel="Entfernen"
          confirmDestructive={true}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={async () => { await softDeleteItem(confirmDelete.id); setConfirmDelete(null); setEditing(null); }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketItemEditSheet — Produkt anlegen/bearbeiten
// ─────────────────────────────────────────────────────────────
function MarketItemEditSheet({ item, dark, t, onClose, onSave, onDelete }) {
  const [name, setName] = useStateM(item?.name || "");
  const [priceStr, setPriceStr] = useStateM(item?.price != null ? String(item.price) : "");
  const [qtyStr, setQtyStr] = useStateM(item?.quantity != null ? String(item.quantity) : "0");
  const [description, setDescription] = useStateM(item?.description || "");
  const [submitting, setSubmitting] = useStateM(false);

  const trimmedName = name.trim();
  const priceNum = parseFloat(String(priceStr).replace(",", "."));
  const qtyNum = parseInt(String(qtyStr).replace(/[^\d-]/g, ""), 10);

  const { validate, errors, clearError, registerRef } = useValidation({
    name:     { required: true },
    priceStr: { required: true, type: "price" },
    qtyStr:   { required: true, type: "stock" },
  });

  const submit = async () => {
    if (submitting) return;
    if (!validate({ name, priceStr, qtyStr })) return;
    setSubmitting(true);
    await onSave({
      name: trimmedName,
      price: priceNum,
      quantity: qtyNum,
      description: description.trim() || null,
      // wish_price bleibt NULL für Standverkauf-Items
    });
    setSubmitting(false);
  };

  const submitFooter = (
    <div>
      <button onClick={submit} disabled={submitting} style={{
        width: "100%", padding: "14px",
        background: t.accent.green.solid,
        color: "#fff",
        border: "none", borderRadius: 14,
        fontFamily: F, fontWeight: 700, fontSize: 15,
        cursor: submitting ? "default" : "pointer",
        boxShadow: `0 6px 20px ${t.accent.green.weak}`,
        opacity: submitting ? 0.7 : 1,
        transition: "opacity 0.18s ease",
      }}>{item ? "Speichern" : "Anlegen"}</button>
      {onDelete && (
        <button onClick={onDelete} style={{
          width: "100%", marginTop: 8, padding: "10px",
          background: "transparent", border: "none",
          color: t.accent.red.solid,
          fontFamily: F, fontWeight: 500, fontSize: 13, cursor: "pointer",
        }}>Produkt entfernen</button>
      )}
    </div>
  );

  return (
    <BottomSheet
      isOpen={true} onClose={onClose}
      size="medium" title={item ? "Produkt bearbeiten" : "Neues Produkt"}
      dark={dark} t={t} F={F}
      stickyFooter={submitFooter}
    >
      <div style={{ padding: "4px 22px 0" }}>
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Name <span style={{ color: t.accent.red.solid }}>*</span>
        </div>
        <div ref={registerRef("name")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: errors.name ? 4 : 14, border: `1px solid ${errors.name ? "rgba(255,59,48,0.6)" : t.glassBorder}` }}>
          <input type="text" value={name} onChange={e => { setName(e.target.value); clearError("name"); }}
            placeholder="z.B. Honig 250g, Käse, Vase …"
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 15, color: t.text, fontFamily: F, minWidth: 0 }}/>
        </div>
        {errors.name && <div style={{ color: "#ff3b30", fontSize: 11, marginBottom: 10, paddingLeft: 4 }}>{errors.name}</div>}

        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Preis € <span style={{ color: t.accent.red.solid }}>*</span>
        </div>
        <div ref={registerRef("priceStr")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: errors.priceStr ? 4 : 14, border: `1px solid ${errors.priceStr ? "rgba(255,59,48,0.6)" : t.glassBorder}`, display: "flex", alignItems: "center", gap: 8 }}>
          <input type="number" inputMode="decimal" step="0.01" min="0" value={priceStr} onChange={e => { setPriceStr(e.target.value); clearError("priceStr"); }}
            placeholder="0,00"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 17, fontWeight: 600, color: t.text, fontFamily: F, minWidth: 0 }}/>
          <span style={{ color: t.textMuted, fontSize: 15 }}>€</span>
        </div>
        {errors.priceStr && <div style={{ color: "#ff3b30", fontSize: 11, marginBottom: 10, paddingLeft: 4 }}>{errors.priceStr}</div>}

        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Lager <span style={{ color: t.accent.red.solid }}>*</span>
        </div>
        <div ref={registerRef("qtyStr")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: errors.qtyStr ? 4 : 14, border: `1px solid ${errors.qtyStr ? "rgba(255,59,48,0.6)" : t.glassBorder}`, display: "flex", alignItems: "center", gap: 8 }}>
          <input type="number" inputMode="numeric" step="1" min="0" value={qtyStr} onChange={e => { setQtyStr(e.target.value); clearError("qtyStr"); }}
            placeholder="0"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 17, fontWeight: 600, color: t.text, fontFamily: F, minWidth: 0 }}/>
          <span style={{ color: t.textMuted, fontSize: 13 }}>Stk</span>
        </div>

        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Notiz
        </div>
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "10px 14px", marginBottom: 8, border: `1px solid ${t.glassBorder}` }}>
          <textarea value={description} onChange={e => setDescription(e.target.value)}
            placeholder="MHD, Charge, Lieferant, …"
            rows={3}
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", fontSize: 14, color: t.text, fontFamily: F, resize: "none", minWidth: 0 }}/>
        </div>
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketRestockSheet — Lager auffüllen
// ─────────────────────────────────────────────────────────────
function MarketRestockSheet({ item, dark, t, onClose, onConfirm }) {
  const [addStr, setAddStr] = useStateM("");
  const [note, setNote] = useStateM("");
  const [submitting, setSubmitting] = useStateM(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  const addNum = parseInt(String(addStr).replace(/[^\d]/g, ""), 10);
  const ready = Number.isFinite(addNum) && addNum > 0;
  const newTotal = (Number(item.quantity) || 0) + (Number.isFinite(addNum) ? addNum : 0);

  const submit = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    await onConfirm(addNum, note.trim() || null);
    setSubmitting(false);
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "20px 22px 22px", zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Lager auffüllen" subtitle={item.name} onClose={onClose} marginBottom={14} />

        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "12px 14px", marginBottom: 12, fontSize: 13, color: t.textSub, border: `1px solid ${t.glassBorder}` }}>
          Aktueller Bestand: <span style={{ color: t.text, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{item.quantity || 0}</span> Stück
        </div>

        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Auffüllen um
        </div>
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: 8, border: `1px solid ${t.glassBorder}`, display: "flex", alignItems: "center", gap: 8 }}>
          <input type="number" inputMode="numeric" step="1" min="1" value={addStr} onChange={e => setAddStr(e.target.value)}
            placeholder="0"
            style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "14px 0", fontSize: 22, fontWeight: 700, color: t.text, fontFamily: F, fontVariantNumeric: "tabular-nums", minWidth: 0, textAlign: "center" }}/>
          <span style={{ color: t.textMuted, fontSize: 14 }}>Stück</span>
        </div>

        <div style={{ textAlign: "center", marginBottom: 16, fontSize: 12, color: ready ? t.accent.green.solid : t.textMuted, fontWeight: 600 }}>
          {ready ? `Neuer Bestand: ${newTotal} Stück` : `Neuer Bestand: ${item.quantity || 0} Stück`}
        </div>

        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
          Notiz <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
        </div>
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "10px 14px", marginBottom: 16, border: `1px solid ${t.glassBorder}` }}>
          <input type="text" value={note} onChange={e => setNote(e.target.value)}
            placeholder="z.B. Charge 2026-04, MHD 12/26, Lieferant…"
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "2px 0", fontSize: 13, color: t.text, fontFamily: F, minWidth: 0 }}/>
        </div>

        <button onClick={submit} disabled={!ready || submitting} style={{
          width: "100%", padding: "14px",
          background: ready ? t.accent.green.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
          color: ready ? "#fff" : t.textMuted,
          border: "none", borderRadius: 14,
          fontFamily: F, fontWeight: 700, fontSize: 15,
          cursor: ready && !submitting ? "pointer" : "not-allowed",
          boxShadow: ready ? `0 6px 20px ${t.accent.green.weak}` : "none",
        }}>{submitting ? "Füge hinzu…" : ready ? `+${addNum} hinzufügen` : "Menge eingeben"}</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketSessionStartSheet — Markt-Tag setup
// ─────────────────────────────────────────────────────────────
function MarketSessionStartSheet({ store, authToken, dark, t, items, lastSession, recentLocations, onClose, onStarted, showToast }) {
  const [location, setLocation] = useStateM("");
  const [mode, setMode] = useStateM("all"); // 'all' | 'pick'
  const [picked, setPicked] = useStateM(new Set());
  const [submitting, setSubmitting] = useStateM(false);
  const { canInstall, install } = usePWAInstall();

  // Default-Sortiment: alle aktiven Items mit quantity > 0
  const defaultSet = useMemoM(() =>
    items.filter(it => it.active !== false && (Number(it.quantity) || 0) > 0).map(it => it.id)
  , [items]);

  const activeItems = useMemoM(() =>
    items.filter(it => it.active !== false).sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"))
  , [items]);

  const toggleItem = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
    haptic(6);
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const ids = mode === "all" ? defaultSet : [...picked];
      const body = {
        location: location.trim() || null,
        starts_at: new Date().toISOString(),
        metadata: { selected_item_ids: ids },
      };
      const session = await apiCall(authToken, `/api/stores/${store.id}/sessions`, { method: "POST", body });
      haptic(20);
      onStarted(session);
    } catch (e) {
      showToast(e.message || "Markt konnte nicht gestartet werden", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const startFooter = (
    <button onClick={submit} disabled={submitting || (mode === "pick" && picked.size === 0)} style={{
      width: "100%", padding: "14px",
      background: "linear-gradient(180deg, #30D158 0%, #1BAE43 100%)",
      border: "none", borderRadius: 14,
      color: "#fff", fontFamily: F, fontWeight: 700, fontSize: 16,
      cursor: submitting ? "default" : "pointer",
      opacity: submitting || (mode === "pick" && picked.size === 0) ? 0.6 : 1,
      boxShadow: "0 6px 22px rgba(48,209,88,0.4)",
    }}>{submitting ? "Starte…" : "Markt starten"}</button>
  );

  return (
    <BottomSheet
      isOpen={true} onClose={onClose}
      size="medium" title="Markt-Tag starten"
      dark={dark} t={t} F={F}
      stickyFooter={startFooter}
    >
      <div style={{ padding: "4px 22px 0" }}>
        {/* Ort */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>Ort</div>
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "0 14px", marginBottom: 8, border: `1px solid ${t.glassBorder}` }}>
          <input type="text" value={location} onChange={e => setLocation(e.target.value)}
            placeholder="z.B. Wochenmarkt Tempelhof"
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", padding: "12px 0", fontSize: 15, color: t.text, fontFamily: F, minWidth: 0 }}/>
        </div>
        {recentLocations.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
            {recentLocations.map(loc => (
              <button key={loc} onClick={() => setLocation(loc)} style={{
                background: t.accent.green.weak, color: t.accent.green.solid,
                border: "none", borderRadius: 8, padding: "5px 10px",
                fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: F,
              }}>{loc}</button>
            ))}
          </div>
        )}

        {/* Sortiment-Mode */}
        <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
          Sortiment
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <button onClick={() => setMode("all")} style={{
            padding: "12px",
            background: mode === "all" ? t.accent.green.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
            color: mode === "all" ? t.accent.green.solid : t.text,
            border: `1.5px solid ${mode === "all" ? t.accent.green.solid : t.glassBorder}`,
            borderRadius: 11, cursor: "pointer", fontFamily: F,
            fontSize: 13, fontWeight: mode === "all" ? 700 : 500,
            textAlign: "left",
          }}>
            <div style={{ fontWeight: 700, marginBottom: 1 }}>Ganzes Sortiment</div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>{defaultSet.length} {defaultSet.length === 1 ? "Produkt" : "Produkte"}</div>
          </button>
          <button onClick={() => setMode("pick")} style={{
            padding: "12px",
            background: mode === "pick" ? t.accent.green.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
            color: mode === "pick" ? t.accent.green.solid : t.text,
            border: `1.5px solid ${mode === "pick" ? t.accent.green.solid : t.glassBorder}`,
            borderRadius: 11, cursor: "pointer", fontFamily: F,
            fontSize: 13, fontWeight: mode === "pick" ? 700 : 500,
            textAlign: "left",
          }}>
            <div style={{ fontWeight: 700, marginBottom: 1 }}>Nur ausgewählte</div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>{picked.size > 0 ? `${picked.size} ausgewählt` : "Tippe Items an"}</div>
          </button>
        </div>

        {/* Multi-Select wenn mode="pick" */}
        {mode === "pick" && (
          activeItems.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: t.textMuted, fontSize: 13 }}>
              Noch keine Produkte im Sortiment.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 8 }}>
              {activeItems.map(it => {
                const sel = picked.has(it.id);
                const out = (Number(it.quantity) || 0) === 0;
                return (
                  <button key={it.id} onClick={() => !out && toggleItem(it.id)} disabled={out} style={{
                    width: "100%",
                    background: sel ? t.accent.green.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
                    border: `1.5px solid ${sel ? t.accent.green.solid : t.glassBorder}`,
                    borderRadius: 11, padding: "10px 12px",
                    cursor: out ? "not-allowed" : "pointer", fontFamily: F, textAlign: "left",
                    display: "flex", alignItems: "center", gap: 10,
                    opacity: out ? 0.4 : 1,
                  }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: `1.5px solid ${sel ? t.accent.green.solid : t.glassBorder}`,
                      background: sel ? t.accent.green.solid : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {sel && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: sel ? 600 : 500, color: sel ? t.accent.green.solid : t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {it.name} {out && <span style={{ color: t.accent.red.solid, fontWeight: 600, fontSize: 11 }}>· ausverkauft</span>}
                    </span>
                    <span style={{ fontSize: 12, color: t.textSub, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
                      <div>{fmtCurrency(it.price)}</div>
                      <div style={{ fontSize: 10 }}>{it.quantity || 0} Stk</div>
                    </span>
                  </button>
                );
              })}
            </div>
          )
        )}
        {/* PWA-Install-Banner (nur auf Android Chrome / Chromium, einmalig) */}
        {canInstall && (
          <div style={{
            margin: "16px 0 4px",
            padding: "12px 14px",
            borderRadius: 12,
            background: dark ? "rgba(10,132,255,0.12)" : "rgba(10,132,255,0.08)",
            border: `1px solid rgba(10,132,255,0.25)`,
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ color: "#0a84ff", flexShrink: 0 }}>
              <rect x="3" y="2" width="14" height="16" rx="3" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 6v6M7 10l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>App installieren</div>
              <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>Für schnelleren Zugriff direkt auf den Startbildschirm</div>
            </div>
            <button onClick={install} style={{
              background: "#0a84ff", border: "none", borderRadius: 8,
              padding: "6px 12px", color: "#fff", fontSize: 12, fontWeight: 600,
              cursor: "pointer", fontFamily: F, flexShrink: 0,
            }}>Installieren</button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketActiveSession — Hauptbildschirm während Markt läuft
// ─────────────────────────────────────────────────────────────
function MarketActiveSession({ store, authToken, dark, t, session, items, onSessionChanged, onItemsChanged, showToast, onBackToHub }) {
  const [sales, setSales] = useStateM([]);
  const [filter, setFilter] = useStateM("");
  const [saleSheetItem, setSaleSheetItem] = useStateM(null);
  const [stornoSale, setStornoSale] = useStateM(null);
  const [bundleOpen, setBundleOpen] = useStateM(false);
  const [endSheetOpen, setEndSheetOpen] = useStateM(false);
  const [actionMenuOpen, setActionMenuOpen] = useStateM(false);
  const [noteSheetOpen, setNoteSheetOpen] = useStateM(false);
  const [loadingSales, setLoadingSales] = useStateM(true);
  const [now, setNow] = useStateM(Date.now());
  useEffectM(() => { const id = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(id); }, []);
  // P-5-Punkte-Fix Block 5: Auto-Refresh + Member-Count
  const [memberCount, setMemberCount] = useStateM(1);
  const [refreshPulse, setRefreshPulse] = useStateM(false);

  const apiBase = `/api/stores/${store.id}`;

  // Sales laden
  useEffectM(() => {
    let cancelled = false;
    (async () => {
      try {
        const detail = await apiCall(authToken, `${apiBase}/sessions/${session.id}`);
        if (!cancelled) setSales(detail?.sales || []);
      } catch (e) {
        if (!cancelled) showToast("Verkäufe konnten nicht geladen werden", "error");
      } finally {
        if (!cancelled) setLoadingSales(false);
      }
    })();
    return () => { cancelled = true; };
  }, [session.id]);

  // Member-Count + Auto-Refresh
  useEffectM(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/members`, { headers: { "Authorization": `Bearer ${authToken}` } });
        if (r.ok) {
          const m = await r.json();
          if (!cancelled) setMemberCount(m.length || 1);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [store.id]);

  useEffectM(() => {
    if (memberCount < 2) return;
    const interval = setInterval(async () => {
      try {
        const detail = await apiCall(authToken, `${apiBase}/sessions/${session.id}`);
        const newSales = detail?.sales || [];
        const oldCount = sales.filter(s => !s.is_voided).length;
        const newCount = newSales.filter(s => !s.is_voided).length;
        setSales(newSales);
        setRefreshPulse(true);
        setTimeout(() => setRefreshPulse(false), 1200);
        if (newCount > oldCount) showToast(`${newCount - oldCount} neue${newCount - oldCount === 1 ? "r" : ""} Verkauf`);
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [memberCount, session.id]);

  // selected_item_ids — wenn leer: alle aktiven Items zeigen (defensive)
  const selectedItemIds = useMemoM(() => {
    const arr = session.metadata?.selected_item_ids;
    return Array.isArray(arr) && arr.length > 0 ? new Set(arr) : null;
  }, [session.metadata]);

  // Sortiment für diese Session — items die mitgenommen wurden
  const sortimentItems = useMemoM(() => {
    const f = filter.trim().toLowerCase();
    const list = items
      .filter(it => it.active !== false)
      .filter(it => !selectedItemIds || selectedItemIds.has(it.id))
      .filter(it => !f || it.name.toLowerCase().includes(f));
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"));
  }, [items, selectedItemIds, filter]);

  // Pro Item: wieviel heute (in dieser Session) verkauft (nicht-storniert)
  const soldQtyByItem = useMemoM(() => {
    const map = new Map();
    for (const sale of sales) {
      if (sale.is_voided) continue;
      for (const si of (sale.sale_items || [])) {
        if (!si.item_id) continue;
        map.set(si.item_id, (map.get(si.item_id) || 0) + Number(si.quantity || 1));
      }
    }
    return map;
  }, [sales]);

  // Letzte Verkäufe (max 8 sichtbar)
  const recentSales = useMemoM(() =>
    [...sales].sort((a, b) => (b.sold_at || "").localeCompare(a.sold_at || "")).slice(0, 8)
  , [sales]);

  const stats = computeMarketStats(sales);

  // Reload-Helper — wird vom sale_synced-Listener genutzt
  const reloadSalesM = async () => {
    try {
      const detail = await apiCall(authToken, `${apiBase}/sessions/${session.id}`);
      setSales(detail?.sales || []);
    } catch {}
  };

  // Sync-Event-Listener — lädt Sales neu wenn Background-Retry für diese Session erfolgreich war
  useEffectM(() => {
    const handler = () => reloadSalesM();
    window.addEventListener("memorica:sale_synced", handler);
    return () => window.removeEventListener("memorica:sale_synced", handler);
  // eslint-disable-next-line
  }, []);

  // Inventory-Helpers für optimistische Updates
  const _applyInventorySubtract = (saleItems) => {
    const updates = new Map();
    for (const si of (saleItems || [])) {
      if (!si.item_id) continue;
      updates.set(si.item_id, (updates.get(si.item_id) || 0) + Number(si.quantity || 1));
    }
    if (updates.size > 0) {
      onItemsChanged(items.map(it => {
        const sub = updates.get(it.id);
        if (!sub || it.quantity == null) return it;
        return { ...it, quantity: Math.max(0, Number(it.quantity) - sub) };
      }));
    }
  };

  const _applyInventoryRestore = (saleItems) => {
    const restores = new Map();
    for (const si of (saleItems || [])) {
      if (!si.item_id) continue;
      restores.set(si.item_id, (restores.get(si.item_id) || 0) + Number(si.quantity || 1));
    }
    if (restores.size > 0) {
      onItemsChanged(items.map(it => {
        const add = restores.get(it.id);
        if (!add || it.quantity == null) return it;
        return { ...it, quantity: Number(it.quantity) + add };
      }));
    }
  };

  // Sale-Handler — POST + optimistisches State-Update + SyncManager
  const completeSale = async (body) => {
    const sm = syncManager;
    const localId = "pending_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);
    const optimistic = {
      id: localId,
      total: body.total,
      payment: body.payment,
      is_bundle: body.is_bundle || false,
      is_voided: false,
      sold_at: new Date().toISOString(),
      sale_items: (body.items || []).map(i => ({
        item_id: i.item_id || null,
        item_name: i.item_name || "",
        unit_price: i.unit_price || 0,
        wish_price: i.wish_price || 0,
        quantity: i.quantity || 1,
      })),
      _pending: true,
    };
    setSales(prev => [optimistic, ...prev]);
    _applyInventorySubtract(optimistic.sale_items);
    haptic(20);

    if (!sm) {
      try {
        const sale = await apiCall(authToken, `${apiBase}/sessions/${session.id}/sales`, { method: "POST", body });
        setSales(prev => prev.map(s => s.id === localId ? sale : s));
        showToast(`Verkauft · ${fmtCurrency(sale.total)}`);
      } catch (e) {
        setSales(prev => prev.filter(s => s.id !== localId));
        _applyInventoryRestore(optimistic.sale_items);
        showToast(e.message || "Verkauf fehlgeschlagen", "error");
      }
      return;
    }

    const result = await sm.write({
      authToken,
      endpoint: `${apiBase}/sessions/${session.id}/sales`,
      method: "POST",
      payload: body,
      action_type: "create_sale",
      store_id: store.id,
    });

    if (result.ok) {
      setSales(prev => prev.map(s => s.id === localId ? result.data : s));
      showToast(`Verkauft · ${fmtCurrency(result.data.total)}`);
    } else if (result.queued) {
      showToast("Offline – Verkauf gespeichert", "warning");
    } else {
      setSales(prev => prev.filter(s => s.id !== localId));
      _applyInventoryRestore(optimistic.sale_items);
      showToast(result.error || "Verkauf fehlgeschlagen", "error");
    }
  };

  // Storno-Handler — PATCH + Lager-Restore + SyncManager
  const stornoSubmit = async (sale) => {
    if (sale._pending) { showToast("Verkauf wird noch synchronisiert", "warning"); return; }

    const sm = syncManager;
    setSales(prev => prev.map(s => s.id === sale.id ? { ...s, is_voided: true, _pending: true } : s));
    _applyInventoryRestore(sale.sale_items);
    haptic(15);

    if (!sm) {
      try {
        const updated = await apiCall(authToken, `${apiBase}/sales/${sale.id}`, { method: "PATCH", body: { is_voided: true } });
        setSales(prev => prev.map(s => s.id === sale.id ? { ...s, ...updated, sale_items: s.sale_items, _pending: false } : s));
        showToast("Verkauf storniert");
      } catch (e) {
        setSales(prev => prev.map(s => s.id === sale.id ? { ...s, is_voided: false, _pending: false } : s));
        _applyInventorySubtract(sale.sale_items);
        showToast(e.message || "Storno fehlgeschlagen", "error");
      }
      return;
    }

    const result = await sm.write({
      authToken,
      endpoint: `${apiBase}/sales/${sale.id}`,
      method: "PATCH",
      payload: { is_voided: true },
      action_type: "void_sale",
      store_id: store.id,
    });

    if (result.ok) {
      setSales(prev => prev.map(s => s.id === sale.id ? { ...s, ...result.data, sale_items: s.sale_items, _pending: false } : s));
      showToast("Verkauf storniert");
    } else if (result.queued) {
      showToast("Offline – Storno gespeichert", "warning");
    } else {
      setSales(prev => prev.map(s => s.id === sale.id ? { ...s, is_voided: false, _pending: false } : s));
      _applyInventorySubtract(sale.sale_items);
      showToast(result.error || "Storno fehlgeschlagen", "error");
    }
  };

  // Notiz-Handler — PATCH session.notes
  const saveNote = async (note) => {
    try {
      const updated = await apiCall(authToken, `${apiBase}/sessions/${session.id}`, { method: "PATCH", body: { notes: note || null } });
      onSessionChanged(updated);
      haptic(10);
      showToast("Notiz gespeichert");
    } catch (e) {
      showToast(e.message || "Notiz konnte nicht gespeichert werden", "error");
    }
  };

  const accent = t.accent.green;

  // Items mit verfügbarem Bestand (für Bundle-Sheet)
  const bundleCandidates = useMemoM(() =>
    sortimentItems.filter(it => (Number(it.quantity) || 0) > 0)
  , [sortimentItems]);

  return (
    <div style={{
      height: "100dvh", maxHeight: "100dvh",
      background: dark ? "#0d1117" : "#dde8f8", fontFamily: F, color: t.text,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
        background: dark ? "rgba(13,17,23,0.85)" : "rgba(221,232,248,0.85)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: `0.5px solid ${t.glassBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBackToHub} aria-label="Übersicht" style={{
            display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
            height: 30, padding: "0 10px 0 8px", borderRadius: 15,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", fontFamily: F,
            fontSize: 12, fontWeight: 600,
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Übersicht
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: accent.solid, animation: "pulse 1.6s ease-in-out infinite" }}/>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text, letterSpacing: "-0.3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {session.location || "Standverkauf"}
              </div>
            </div>
            <div style={{ fontSize: 11, color: t.textSub, marginTop: 1, display: "flex", alignItems: "center", gap: 4 }}>
              {fmtSinceM(session.starts_at)}
              {memberCount >= 2 && (
                <span aria-label="Sync aktiv" style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: refreshPulse ? t.accent.green.solid : t.textMuted,
                  opacity: refreshPulse ? 1 : 0.4,
                  transition: "all 0.4s ease",
                  marginLeft: 4,
                }}/>
              )}
            </div>
          </div>
          <SyncIndicator dark={dark} />
          <button onClick={() => { setActionMenuOpen(true); haptic(8); }} aria-label="Mehr" style={{
            width: 30, height: 30, borderRadius: 15, flexShrink: 0,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><circle cx="3" cy="8" r="1.4"/><circle cx="8" cy="8" r="1.4"/><circle cx="13" cy="8" r="1.4"/></svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {/* Stats Hero */}
        <div style={{ padding: "20px 16px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 36, fontWeight: 700, color: t.text, letterSpacing: "-1px", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
            {fmtCurrency(stats.total)}
          </div>
          <div style={{ fontSize: 13, color: t.textSub, marginTop: 4 }}>
            {stats.count} {stats.count === 1 ? "Verkauf" : "Verkäufe"}
            {stats.bundleCount > 0 && ` · ${stats.bundleCount} Bundle`}
          </div>
          {(stats.cash > 0 || stats.card > 0) && (
            <div style={{ display: "inline-flex", gap: 12, marginTop: 8, fontSize: 12, color: t.textMuted }}>
              {stats.cash > 0 && <span>Bar {fmtCurrency(stats.cash)}</span>}
              {stats.card > 0 && <span>Karte {fmtCurrency(stats.card)}</span>}
            </div>
          )}
          {stats.totalDiscount > 0 && (
            <div style={{ fontSize: 11, color: t.accent.orange.solid, marginTop: 6, fontWeight: 600 }}>
              −{fmtCurrency(stats.totalDiscount)} Rabatt gegeben ({stats.discountedSales} {stats.discountedSales === 1 ? "Verkauf" : "Verkäufe"})
            </div>
          )}
        </div>

        {/* Sortiment */}
        <div style={{ padding: "12px 16px 0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Sortiment · {sortimentItems.length}
            </div>
          </div>
          {sortimentItems.length > 5 && (
            <div style={{ marginBottom: 10, background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", borderRadius: 10, padding: "0 12px", display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: t.textMuted, flexShrink: 0 }}>
                <circle cx="6" cy="6" r="4"/><path d="m12 12-3-3" strokeLinecap="round"/>
              </svg>
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filtern …"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "10px 0", fontSize: 14, color: t.text, fontFamily: F, minWidth: 0 }}/>
              {filter && (
                <button onClick={() => setFilter("")} style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", padding: 4 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
                </button>
              )}
            </div>
          )}
          {sortimentItems.length === 0 ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: t.textMuted, fontSize: 13 }}>
              {filter ? "Nichts gefunden." : "Keine Produkte mitgenommen."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {sortimentItems.map(it => {
                const q = Number(it.quantity) || 0;
                const out = q === 0;
                const soldHere = soldQtyByItem.get(it.id) || 0;
                return (
                  <div key={it.id} style={{
                    background: out ? (dark ? "rgba(255,69,58,0.06)" : "rgba(255,69,58,0.04)") : (dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)"),
                    border: `1px solid ${out ? t.accent.red.solid + "30" : t.glassBorder}`,
                    borderRadius: 12, padding: "10px 12px",
                    display: "flex", alignItems: "center", gap: 10,
                    opacity: out ? 0.7 : 1,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                      background: out ? t.accent.red.weak : t.accent.green.weak,
                      color: out ? t.accent.red.solid : t.accent.green.solid,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                    }}>
                      {q}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>{it.name}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmtCurrency(it.price)}</div>
                      </div>
                      <div style={{ fontSize: 11, color: out ? t.accent.red.solid : t.textSub, marginTop: 1, fontWeight: out ? 600 : 400 }}>
                        {out ? "AUSVERKAUFT" : `${q} ${q === 1 ? "Stück" : "Stück"}`}
                        {soldHere > 0 && <span style={{ marginLeft: 6, color: t.accent.green.solid, fontWeight: 600 }}>· {soldHere} verkauft</span>}
                      </div>
                    </div>
                    <button onClick={() => { if (!out) { setSaleSheetItem(it); haptic(8); } }} disabled={out} style={{
                      flexShrink: 0,
                      padding: "8px 14px",
                      background: out ? (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)") : t.accent.green.solid,
                      color: out ? t.textMuted : "#fff",
                      border: "none", borderRadius: 9,
                      fontFamily: F, fontWeight: 700, fontSize: 13,
                      cursor: out ? "not-allowed" : "pointer",
                      boxShadow: out ? "none" : `0 2px 8px ${t.accent.green.weak}`,
                    }}>+ Verkauf</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Letzte Verkäufe */}
        {recentSales.length > 0 && (
          <div style={{ padding: "8px 16px 0" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
              Letzte Verkäufe
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {recentSales.map(sale => {
                const isVoided = sale.is_voided;
                const isPending = sale._pending;
                const sis = sale.sale_items || [];
                const totalQty = sis.reduce((s, i) => s + Number(i.quantity || 1), 0);
                const headline = sale.is_bundle
                  ? `Bundle (${sis.length} Produkte · ${totalQty} Stk)`
                  : sis.length > 0
                    ? (Number(sis[0].quantity || 1) > 1 ? `${sis[0].quantity}× ${sis[0].item_name}` : sis[0].item_name)
                    : "Verkauf";
                const stdSum = sis.reduce((s, i) => {
                  const ref = sale.is_bundle ? Number(i.wish_price) : Number(i.unit_price);
                  return s + (Number.isFinite(ref) ? ref * Number(i.quantity || 1) : 0);
                }, 0);
                const discount = stdSum > Number(sale.total) ? stdSum - Number(sale.total) : 0;
                return (
                  <button key={sale.id} onClick={() => !isPending && setStornoSale(sale)} style={{
                    width: "100%",
                    background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.5)",
                    border: `1px solid ${isPending ? t.accent.orange.solid + "55" : t.glassBorder}`,
                    borderRadius: 12, padding: "10px 12px",
                    cursor: isPending ? "default" : "pointer", fontFamily: F, textAlign: "left",
                    display: "flex", alignItems: "center", gap: 10,
                    opacity: isVoided ? 0.5 : isPending ? 0.75 : 1,
                    textDecoration: isVoided ? "line-through" : "none",
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                      background: isVoided ? t.accent.red.weak : isPending ? "rgba(255,159,10,0.15)" : t.accent.green.weak,
                      color: isVoided ? t.accent.red.solid : isPending ? "#ff9f0a" : t.accent.green.solid,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {isVoided ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                      ) : isPending ? (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ animation: "ptrSpin 1.2s linear infinite" }}>
                          <path d="M6 1.5A4.5 4.5 0 0 1 10.5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          <path d="M6 10.5A4.5 4.5 0 0 1 1.5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6.5L5 9.5L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {headline}
                      </div>
                      <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>
                        {fmtAgoM(sale.sold_at)} · {sale.payment === "cash" ? "Bar" : sale.payment === "card" ? "Karte" : "Andere"}
                        {discount > 0 && !isVoided && (
                          <span style={{ marginLeft: 6, color: t.accent.orange.solid }}>· −{fmtCurrency(discount)}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums" }}>
                      {fmtCurrency(sale.total)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bundle-Button */}
        <div style={{ padding: "8px 16px 16px" }}>
          <button onClick={() => { setBundleOpen(true); haptic(8); }} style={{
            width: "100%", padding: "13px",
            background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
            border: `1px solid ${t.glassBorder}`, borderRadius: 12,
            color: t.text, fontFamily: F, fontWeight: 600, fontSize: 14,
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
              <path d="M2 5L8 2L14 5V11L8 14L2 11V5Z"/><path d="M2 5L8 8M8 8L14 5M8 8V14"/>
            </svg>
            Bundle-Verkauf
          </button>
        </div>

        {/* Markt beenden */}
        <div style={{ padding: "8px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)" }}>
          <button onClick={() => { setEndSheetOpen(true); haptic(10); }} style={{
            width: "100%", padding: "14px",
            background: t.accent.red.weak,
            border: `1px solid ${t.accent.red.solid}40`, borderRadius: 12,
            color: t.accent.red.solid, fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: "pointer",
          }}>Markt beenden</button>
        </div>
      </div>

      {/* Sheets */}
      {saleSheetItem && (
        <MarketSaleSheet
          item={saleSheetItem} dark={dark} t={t}
          onClose={() => setSaleSheetItem(null)}
          onConfirm={async (data) => {
            await completeSale({
              total: data.total,
              payment: data.payment,
              items: [{
                item_id: saleSheetItem.id,
                item_name: saleSheetItem.name,
                quantity: data.quantity,
                unit_price: data.standardPrice,
                wish_price: null,
              }],
            });
            setSaleSheetItem(null);
          }}
          showToast={showToast}
        />
      )}
      {bundleOpen && (
        <MarketBundleSheet
          availableItems={bundleCandidates} dark={dark} t={t}
          onClose={() => setBundleOpen(false)}
          onConfirm={async (data) => {
            await completeSale({
              total: data.total,
              payment: data.payment,
              is_bundle: true,
              items: data.items.map(({ item, qty }) => ({
                item_id: item.id,
                item_name: item.name,
                quantity: qty,
                unit_price: null, // Bundle-Aufteilung später nachtragbar
                wish_price: Number(item.price) || null, // Standard-Preis pro Stück für Stat
              })),
            });
            setBundleOpen(false);
          }}
          showToast={showToast}
        />
      )}
      {stornoSale && (
        <MarketSaleStornoSheet
          sale={stornoSale} dark={dark} t={t}
          onClose={() => setStornoSale(null)}
          onConfirm={async () => {
            await stornoSubmit(stornoSale);
            setStornoSale(null);
          }}
        />
      )}
      {endSheetOpen && (
        <MarketSessionEndSheet
          store={store} authToken={authToken} dark={dark} t={t}
          session={session}
          sales={sales}
          items={items}
          selectedItemIds={selectedItemIds}
          onClose={() => setEndSheetOpen(false)}
          onEnded={(updatedSession) => {
            setEndSheetOpen(false);
            onSessionChanged(updatedSession);
          }}
          showToast={showToast}
        />
      )}
      {actionMenuOpen && (
        <ActionMenu
          dark={dark} t={t}
          onClose={() => setActionMenuOpen(false)}
          accentColor={t.accent.green}
          actions={[
            { label: session.notes ? "Notiz bearbeiten" : "Notiz hinzufügen", icon: "note", onClick: () => { setActionMenuOpen(false); setNoteSheetOpen(true); } },
            { label: "Markt beenden", icon: "stop", destructive: true, onClick: () => { setActionMenuOpen(false); setEndSheetOpen(true); } },
          ]}
        />
      )}
      {noteSheetOpen && (
        <MarketAddNoteSheet
          dark={dark} t={t}
          initialNote={session.notes || ""}
          onClose={() => setNoteSheetOpen(false)}
          onSave={async (note) => { await saveNote(note); setNoteSheetOpen(false); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketSaleSheet — Einzel-Verkauf mit Mengen-Stepper + optional Rabatt
// ─────────────────────────────────────────────────────────────
function MarketSaleSheet({ item, dark, t, onClose, onConfirm, showToast }) {
  const stockMax = Number(item.quantity) || 0;
  const [qty, setQty] = useStateM(1);
  const [discountOpen, setDiscountOpen] = useStateM(false);
  const [customPriceStr, setCustomPriceStr] = useStateM("");
  const [submitting, setSubmitting] = useStateM(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();

  const standardPrice = Number(item.price) || 0;
  const standardTotal = standardPrice * qty;
  const customPriceNum = parseFloat(String(customPriceStr).replace(",", "."));
  const useCustom = discountOpen && Number.isFinite(customPriceNum) && customPriceNum >= 0;
  const finalTotal = useCustom ? customPriceNum : standardTotal;
  const discount = standardTotal - finalTotal;
  const discountPct = standardTotal > 0 ? Math.round((discount / standardTotal) * 100) : 0;

  const ready = qty > 0 && qty <= stockMax && finalTotal >= 0;

  const submit = async (payment) => {
    if (!ready || submitting) return;
    if (payment === "card") {
      showToast("Karten-Zahlung kommt mit der nächsten App-Version (SumUp)", "info");
      return;
    }
    setSubmitting(true);
    await onConfirm({
      total: finalTotal,
      payment,
      quantity: qty,
      standardPrice,
    });
    setSubmitting(false);
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 400, radius: 24, padding: "20px 22px 22px", zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Verkaufen" subtitle={item.name} onClose={onClose} marginBottom={14} />

        {/* Standard-Preis */}
        <div style={{ textAlign: "center", marginBottom: 8, fontSize: 12, color: t.textMuted }}>
          Standard-Preis: <span style={{ color: t.text, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(standardPrice)}</span>
        </div>

        {/* Mengen-Stepper */}
        <div style={{
          background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          borderRadius: 14, padding: "10px 14px",
          marginBottom: 10, border: `1px solid ${t.glassBorder}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        }}>
          <button onClick={() => { if (qty > 1) { setQty(qty - 1); haptic(6); } }} disabled={qty <= 1} style={{
            width: 38, height: 38, borderRadius: 19,
            background: qty > 1 ? t.accent.green.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: qty > 1 ? "#fff" : t.textMuted,
            border: "none", cursor: qty > 1 ? "pointer" : "not-allowed",
            fontSize: 22, fontWeight: 700, fontFamily: F,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>−</button>
          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{qty}</div>
            <div style={{ fontSize: 11, color: t.textSub, marginTop: 4 }}>von {stockMax} verfügbar</div>
          </div>
          <button onClick={() => { if (qty < stockMax) { setQty(qty + 1); haptic(6); } }} disabled={qty >= stockMax} style={{
            width: 38, height: 38, borderRadius: 19,
            background: qty < stockMax ? t.accent.green.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: qty < stockMax ? "#fff" : t.textMuted,
            border: "none", cursor: qty < stockMax ? "pointer" : "not-allowed",
            fontSize: 22, fontWeight: 700, fontFamily: F,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>+</button>
        </div>

        {/* Live-Total */}
        <div style={{ textAlign: "center", marginBottom: 12, fontSize: 13, color: t.textSub, fontVariantNumeric: "tabular-nums" }}>
          {qty} × {fmtCurrency(standardPrice)} = <span style={{ color: t.text, fontWeight: 700 }}>{fmtCurrency(standardTotal)}</span>
        </div>

        {/* Rabatt-Toggle */}
        <button onClick={() => { setDiscountOpen(!discountOpen); haptic(6); if (!discountOpen && !customPriceStr) setCustomPriceStr(String(standardTotal)); }} style={{
          width: "100%", padding: "8px 14px", marginBottom: 8,
          background: discountOpen ? t.accent.orange.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"),
          border: `1px solid ${discountOpen ? t.accent.orange.solid + "40" : t.glassBorder}`,
          borderRadius: 11,
          color: discountOpen ? t.accent.orange.solid : t.textSub,
          fontFamily: F, fontWeight: 600, fontSize: 12,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        }}>
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: discountOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.18s ease" }}>
            <path d="M2 4L6 8L10 4"/>
          </svg>
          Preis anpassen
        </button>

        {/* Rabatt-Bereich (collapsible) */}
        {discountOpen && (
          <div style={{ marginBottom: 12 }}>
            <div style={{
              background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
              borderRadius: 12, padding: "10px 14px", marginBottom: 6,
              border: `1px solid ${t.glassBorder}`,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={customPriceStr} onChange={e => setCustomPriceStr(e.target.value)}
                placeholder={String(standardTotal)}
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "8px 0", fontSize: 22, fontWeight: 700, color: t.text, fontFamily: F, fontVariantNumeric: "tabular-nums", minWidth: 0, textAlign: "center" }}/>
              <span style={{ color: t.textMuted, fontSize: 17, fontWeight: 600 }}>€</span>
            </div>
            {useCustom && discount > 0 && (
              <div style={{ textAlign: "center", fontSize: 12, color: t.accent.orange.solid, fontWeight: 600 }}>
                Rabatt: −{fmtCurrency(discount)} ({-discountPct}%)
              </div>
            )}
            {useCustom && discount < 0 && (
              <div style={{ textAlign: "center", fontSize: 12, color: t.accent.green.solid, fontWeight: 600 }}>
                Aufpreis: +{fmtCurrency(-discount)} (+{-discountPct}%)
              </div>
            )}
          </div>
        )}

        {/* Bezahlung */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <button onClick={() => submit("cash")} disabled={!ready || submitting} style={{
            padding: "16px",
            background: ready ? t.accent.green.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: ready ? "#fff" : t.textMuted,
            border: "none", borderRadius: 14,
            fontFamily: F, fontWeight: 700, fontSize: 15,
            cursor: ready && !submitting ? "pointer" : "not-allowed",
            boxShadow: ready ? `0 6px 20px ${t.accent.green.weak}` : "none",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
          }}>
            <span>Bar</span>
            <span style={{ fontSize: 11, opacity: 0.85, fontWeight: 600 }}>{fmtCurrency(finalTotal)}</span>
          </button>
          <button onClick={() => submit("card")} disabled={!ready || submitting} style={{
            padding: "16px",
            background: ready ? t.accent.blue.solid : (dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
            color: ready ? "#fff" : t.textMuted,
            border: "none", borderRadius: 14,
            fontFamily: F, fontWeight: 700, fontSize: 15,
            cursor: ready && !submitting ? "pointer" : "not-allowed",
            boxShadow: ready ? `0 6px 20px rgba(10,132,255,0.35)` : "none",
            opacity: 0.85,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
          }}>
            <span>Karte</span>
            <span style={{ fontSize: 11, opacity: 0.85, fontWeight: 600 }}>{fmtCurrency(finalTotal)}</span>
          </button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketBundleSheet — Bundle-Verkauf mit Mengen
// ─────────────────────────────────────────────────────────────
function MarketBundleSheet({ availableItems, dark, t, onClose, onConfirm, showToast }) {
  const [qtyMap, setQtyMap] = useStateM(new Map()); // item.id → qty
  const [bundlePriceStr, setBundlePriceStr] = useStateM("");
  const [submitting, setSubmitting] = useStateM(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const { validate: validateBundle, errors: bundleErrors, clearError: clearBundleError, registerRef: registerBundleRef } = useValidation({
    bundlePriceStr: { required: true, type: "price_gte0" },
  });

  const setQty = (id, val) => {
    const next = new Map(qtyMap);
    if (val <= 0) next.delete(id); else next.set(id, val);
    setQtyMap(next);
    haptic(6);
  };
  const inc = (it) => {
    const cur = qtyMap.get(it.id) || 0;
    if (cur < (Number(it.quantity) || 0)) setQty(it.id, cur + 1);
  };
  const dec = (it) => {
    const cur = qtyMap.get(it.id) || 0;
    if (cur > 0) setQty(it.id, cur - 1);
  };

  // Standard-Summe = sum(qty × price)
  const standardSum = useMemoM(() => {
    let s = 0;
    for (const [id, qty] of qtyMap.entries()) {
      const it = availableItems.find(x => x.id === id);
      if (it) s += (Number(it.price) || 0) * qty;
    }
    return s;
  }, [qtyMap, availableItems]);

  const totalStk = useMemoM(() => [...qtyMap.values()].reduce((s, q) => s + q, 0), [qtyMap]);
  const usedItems = useMemoM(() =>
    [...qtyMap.entries()]
      .map(([id, qty]) => ({ item: availableItems.find(x => x.id === id), qty }))
      .filter(x => x.item)
  , [qtyMap, availableItems]);

  const priceNum = parseFloat(String(bundlePriceStr).replace(",", "."));
  const ready = usedItems.length >= 2 && Number.isFinite(priceNum) && priceNum >= 0;
  const discount = ready && standardSum > priceNum ? standardSum - priceNum : 0;
  const discountPct = standardSum > 0 ? Math.round((discount / standardSum) * 100) : 0;

  const submit = async (payment) => {
    if (submitting) return;
    if (payment === "card") {
      showToast("Karten-Zahlung kommt mit der nächsten App-Version (SumUp)", "info");
      return;
    }
    if (!validateBundle({ bundlePriceStr })) return;
    if (usedItems.length < 2) {
      showToast("Mindestens 2 Produkte wählen", "error");
      return;
    }
    setSubmitting(true);
    await onConfirm({ total: priceNum, payment, items: usedItems });
    setSubmitting(false);
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 480, radius: 24, padding: 0, zIndex: 93 })}>
        <div style={{ padding: "20px 22px 12px" }}>
          <ModalHeader t={t} dark={dark} F={F} title="Bundle-Verkauf"
            subtitle={totalStk > 0 ? `${totalStk} ${totalStk === 1 ? "Stück" : "Stück"} · Standard ${fmtCurrency(standardSum)}` : "Mengen wählen"}
            onClose={onClose} marginBottom={12} />
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "0 22px", maxHeight: Math.max(180, vh - 380) }}>
          {availableItems.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: t.textMuted, fontSize: 13 }}>Keine Produkte verfügbar.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 12 }}>
              {availableItems.map(it => {
                const qty = qtyMap.get(it.id) || 0;
                const sel = qty > 0;
                const stockMax = Number(it.quantity) || 0;
                return (
                  <div key={it.id} style={{
                    background: sel ? t.accent.green.weak : (dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
                    border: `1.5px solid ${sel ? t.accent.green.solid : t.glassBorder}`,
                    borderRadius: 11, padding: "10px 12px",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: sel ? 600 : 500, color: sel ? t.accent.green.solid : t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.name}</div>
                      <div style={{ fontSize: 11, color: t.textSub, marginTop: 1, fontVariantNumeric: "tabular-nums" }}>
                        {fmtCurrency(it.price)} · {stockMax} verfügbar
                      </div>
                    </div>
                    <button onClick={() => dec(it)} disabled={qty <= 0} style={{
                      width: 28, height: 28, borderRadius: 14,
                      background: qty > 0 ? t.accent.green.solid : "transparent",
                      color: qty > 0 ? "#fff" : t.textMuted,
                      border: qty > 0 ? "none" : `1px solid ${t.glassBorder}`,
                      cursor: qty > 0 ? "pointer" : "not-allowed",
                      fontSize: 17, fontWeight: 700, fontFamily: F,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>−</button>
                    <div style={{ minWidth: 22, textAlign: "center", fontSize: 15, fontWeight: 700, color: sel ? t.accent.green.solid : t.text, fontVariantNumeric: "tabular-nums" }}>
                      {qty}
                    </div>
                    <button onClick={() => inc(it)} disabled={qty >= stockMax} style={{
                      width: 28, height: 28, borderRadius: 14,
                      background: qty < stockMax ? t.accent.green.solid : "transparent",
                      color: qty < stockMax ? "#fff" : t.textMuted,
                      border: qty < stockMax ? "none" : `1px solid ${t.glassBorder}`,
                      cursor: qty < stockMax ? "pointer" : "not-allowed",
                      fontSize: 17, fontWeight: 700, fontFamily: F,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>+</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 22px calc(env(safe-area-inset-bottom, 14px) + 14px)", borderTop: `0.5px solid ${t.divider}` }}>
          <div ref={registerBundleRef("bundlePriceStr")} style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "10px 14px", marginBottom: bundleErrors.bundlePriceStr ? 4 : 4, border: `1px solid ${bundleErrors.bundlePriceStr ? "rgba(255,59,48,0.6)" : t.glassBorder}`, display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" inputMode="decimal" step="0.01" min="0" value={bundlePriceStr} onChange={e => { setBundlePriceStr(e.target.value); clearBundleError("bundlePriceStr"); }}
              placeholder="Bundle-Preis"
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "8px 0", fontSize: 22, fontWeight: 700, color: t.text, fontFamily: F, fontVariantNumeric: "tabular-nums", minWidth: 0, textAlign: "center" }}/>
            <span style={{ color: t.textMuted, fontSize: 18, fontWeight: 600 }}>€</span>
          </div>
          {bundleErrors.bundlePriceStr && <div style={{ fontSize: 11, color: "rgba(255,59,48,0.9)", marginBottom: 4, paddingLeft: 4 }}>{bundleErrors.bundlePriceStr}</div>}
          <div style={{ textAlign: "center", marginBottom: 10, fontSize: 11, fontWeight: 600, minHeight: 14 }}>
            {ready && discount > 0 && (
              <span style={{ color: t.accent.orange.solid }}>−{fmtCurrency(discount)} ({-discountPct}%)</span>
            )}
            {ready && discount === 0 && (
              <span style={{ color: t.textMuted }}>= Standard-Summe</span>
            )}
            {ready && discount < 0 && (
              <span style={{ color: t.accent.green.solid }}>+{fmtCurrency(-discount)} ({-discountPct}%)</span>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <button onClick={() => submit("cash")} disabled={submitting} style={{
              padding: "14px",
              background: t.accent.green.solid,
              color: "#fff",
              border: "none", borderRadius: 12,
              fontFamily: F, fontWeight: 700, fontSize: 14,
              cursor: submitting ? "not-allowed" : "pointer",
            }}>Bar</button>
            <button onClick={() => submit("card")} disabled={submitting} style={{
              padding: "14px",
              background: t.accent.blue.solid,
              color: "#fff",
              border: "none", borderRadius: 12,
              fontFamily: F, fontWeight: 700, fontSize: 14,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: 0.85,
            }}>Karte</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketSaleStornoSheet — Verkauf stornieren
// ─────────────────────────────────────────────────────────────
function MarketSaleStornoSheet({ sale, dark, t, onClose, onConfirm }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [submitting, setSubmitting] = useStateM(false);
  const sis = sale.sale_items || [];
  const isVoided = sale.is_voided;

  const submit = async () => {
    if (submitting || isVoided) return;
    setSubmitting(true);
    await onConfirm();
    setSubmitting(false);
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 380, radius: 24, padding: "20px 22px 22px", zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Verkauf-Details" onClose={onClose} marginBottom={12} />

        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: 14, marginBottom: 14, border: `1px solid ${t.glassBorder}` }}>
          {sis.map((si, i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: i === 0 ? "0 0 8px" : "8px 0",
              borderTop: i > 0 ? `0.5px solid ${t.divider}` : "none",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {Number(si.quantity) > 1 ? `${si.quantity}× ` : ""}{si.item_name}
                </div>
                {si.unit_price != null && Number(si.quantity) > 1 && (
                  <div style={{ fontSize: 11, color: t.textSub, marginTop: 1, fontVariantNumeric: "tabular-nums" }}>
                    {fmtCurrency(si.unit_price)} pro Stück
                  </div>
                )}
              </div>
            </div>
          ))}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            paddingTop: 10, marginTop: 8, borderTop: `0.5px solid ${t.divider}`,
            fontSize: 14, fontWeight: 700, color: t.text,
          }}>
            <span>Total</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(sale.total)}</span>
          </div>
        </div>

        <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", borderRadius: 10, padding: "8px 12px", marginBottom: 16, fontSize: 12, color: t.textSub, display: "flex", justifyContent: "space-between" }}>
          <span>{fmtTimeM(sale.sold_at)} · {fmtDateM(sale.sold_at)}</span>
          <span>{sale.payment === "cash" ? "Bar" : sale.payment === "card" ? "Karte" : "Andere"}</span>
        </div>

        {isVoided ? (
          <div style={{ textAlign: "center", padding: "14px", background: t.accent.red.weak, color: t.accent.red.solid, borderRadius: 12, fontSize: 13, fontWeight: 600 }}>
            Bereits storniert
          </div>
        ) : (
          <button onClick={submit} disabled={submitting} style={{
            width: "100%", padding: "13px",
            background: t.accent.red.solid, color: "#fff",
            border: "none", borderRadius: 12,
            fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: submitting ? "default" : "pointer",
            opacity: submitting ? 0.6 : 1,
          }}>{submitting ? "Storniere…" : "Stornieren"}</button>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketSessionEndSheet — Tagesabschluss
// ─────────────────────────────────────────────────────────────
function MarketSessionEndSheet({ store, authToken, dark, t, session, sales, items, selectedItemIds, onClose, onEnded, showToast }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [note, setNote] = useStateM(session.notes || "");
  const [submitting, setSubmitting] = useStateM(false);
  const stats = computeMarketStats(sales);

  // Pro Item: Stk verkauft + Umsatz (Bundles werden aufgeteilt: jedes sale_item zählt)
  const perItem = useMemoM(() => {
    const map = new Map(); // id → { qty, revenue, name }
    for (const sale of sales) {
      if (sale.is_voided) continue;
      const sis = sale.sale_items || [];
      // Für Bundles: anteiliger Umsatz pro Item via wish_price-Verteilung
      let bundleStdSum = 0;
      if (sale.is_bundle) {
        bundleStdSum = sis.reduce((s, i) => s + (Number(i.wish_price) || 0) * Number(i.quantity || 1), 0);
      }
      for (const si of sis) {
        if (!si.item_id) continue;
        const cur = map.get(si.item_id) || { qty: 0, revenue: 0, name: si.item_name };
        cur.qty += Number(si.quantity || 1);
        if (sale.is_bundle) {
          // Anteiliger Bundle-Umsatz: (item-std / bundle-std) × actual total
          const itemStd = (Number(si.wish_price) || 0) * Number(si.quantity || 1);
          if (bundleStdSum > 0) {
            cur.revenue += (itemStd / bundleStdSum) * Number(sale.total || 0);
          }
        } else {
          // Einzel-Sale: anteiliger Umsatz proportional zur Menge
          const itemStd = (Number(si.unit_price) || 0) * Number(si.quantity || 1);
          const totalStd = sis.reduce((s, x) => s + (Number(x.unit_price) || 0) * Number(x.quantity || 1), 0);
          cur.revenue += totalStd > 0 ? (itemStd / totalStd) * Number(sale.total || 0) : 0;
        }
        cur.name = si.item_name;
        map.set(si.item_id, cur);
      }
    }
    return [...map.entries()].map(([id, v]) => ({ id, ...v }));
  }, [sales]);

  const topProducts = useMemoM(() =>
    [...perItem].sort((a, b) => b.qty - a.qty).slice(0, 3)
  , [perItem]);

  // Ausverkaufte Produkte zum Zeitpunkt jetzt (quantity = 0 + war im Sortiment)
  const justEmpty = useMemoM(() => {
    const sortimentSet = selectedItemIds || new Set(items.map(i => i.id));
    return items.filter(it => sortimentSet.has(it.id) && (Number(it.quantity) || 0) === 0 && perItem.find(p => p.id === it.id));
  }, [items, selectedItemIds, perItem]);

  // Lager-Diff: für jedes verkaufte Item: berechne vorher = jetzt + qty
  const stockDiff = useMemoM(() => perItem.map(p => {
    const it = items.find(x => x.id === p.id);
    if (!it) return null;
    const after = Number(it.quantity) || 0;
    const before = after + p.qty;
    return { name: p.name, before, after };
  }).filter(Boolean), [perItem, items]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const body = {
        ends_at: new Date().toISOString(),
        notes: note.trim() || null,
      };
      const updated = await apiCall(authToken, `/api/stores/${store.id}/sessions/${session.id}`, { method: "PATCH", body });
      haptic(20);
      showToast("Markt beendet");
      onEnded(updated);
    } catch (e) {
      showToast(e.message || "Markt konnte nicht beendet werden", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 460, radius: 24, padding: 0, zIndex: 93 })}>
        <div style={{ padding: "20px 22px 12px" }}>
          <ModalHeader t={t} dark={dark} F={F} title="Markt beenden"
            subtitle={`${session.location || "Standverkauf"} · ${fmtDurationM(session.starts_at)}`}
            onClose={onClose} marginBottom={14} />
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "0 22px", maxHeight: Math.max(220, vh - 360) }}>
          {/* Hero-Stats */}
          <div style={{
            background: dark ? "rgba(48,209,88,0.08)" : "rgba(48,209,88,0.06)",
            border: `1px solid ${t.accent.green.solid}30`,
            borderRadius: 14, padding: "16px 18px", marginBottom: 14,
            textAlign: "center",
          }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{fmtCurrency(stats.total)}</div>
            <div style={{ fontSize: 12, color: t.textSub, marginTop: 4 }}>
              {stats.count} {stats.count === 1 ? "Verkauf" : "Verkäufe"}
              {stats.bundleCount > 0 && ` · davon ${stats.bundleCount} Bundle`}
            </div>
            {(stats.cash > 0 || stats.card > 0) && (
              <div style={{ display: "inline-flex", gap: 12, marginTop: 8, fontSize: 11, color: t.textMuted }}>
                {stats.cash > 0 && <span>Bar {fmtCurrency(stats.cash)}</span>}
                {stats.card > 0 && <span>Karte {fmtCurrency(stats.card)}</span>}
              </div>
            )}
            {stats.totalDiscount > 0 && (
              <div style={{ fontSize: 11, color: t.accent.orange.solid, marginTop: 6, fontWeight: 600 }}>
                −{fmtCurrency(stats.totalDiscount)} Rabatt insgesamt
              </div>
            )}
          </div>

          {/* Top-Produkte */}
          {topProducts.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
                Top-Produkte
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {topProducts.map((p, i) => (
                  <div key={p.id} style={{
                    background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                    border: `1px solid ${t.glassBorder}`,
                    borderRadius: 10, padding: "10px 12px",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                      background: i === 0 ? t.accent.orange.weak : t.accent.green.weak,
                      color: i === 0 ? t.accent.orange.solid : t.accent.green.solid,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700,
                    }}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: t.textSub, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
                      <div style={{ color: t.text, fontWeight: 700 }}>{p.qty}× {fmtCurrency(p.revenue)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Ausverkauft */}
          {justEmpty.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.accent.red.solid, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
                Ausverkauft
              </div>
              <div style={{ background: t.accent.red.weak, border: `1px solid ${t.accent.red.solid}30`, borderRadius: 10, padding: "8px 12px" }}>
                {justEmpty.map((it, i) => (
                  <div key={it.id} style={{ fontSize: 13, color: t.text, padding: "4px 0", borderTop: i > 0 ? `0.5px solid ${t.accent.red.solid}20` : "none" }}>
                    {it.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lager-Diff */}
          {stockDiff.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8, paddingLeft: 4 }}>
                Lager nach Markt
              </div>
              <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", border: `1px solid ${t.glassBorder}`, borderRadius: 10, padding: "8px 12px" }}>
                {stockDiff.map((d, i) => (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "5px 0",
                    borderTop: i > 0 ? `0.5px solid ${t.divider}` : "none",
                    fontSize: 12,
                  }}>
                    <span style={{ color: t.text, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.name}</span>
                    <span style={{ color: t.textMuted, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{d.before} → <span style={{ color: t.text, fontWeight: 600 }}>{d.after}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notiz */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, paddingLeft: 4 }}>
              Notiz <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span>
            </div>
            <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "10px 14px", border: `1px solid ${t.glassBorder}` }}>
              <textarea value={note} onChange={e => setNote(e.target.value)}
                placeholder="Wetter, Besucher-Andrang, …"
                rows={2}
                style={{ width: "100%", background: "transparent", border: "none", outline: "none", fontSize: 13, color: t.text, fontFamily: F, resize: "none", minWidth: 0 }}/>
            </div>
          </div>
        </div>

        <div style={{ padding: "12px 22px calc(env(safe-area-inset-bottom, 14px) + 14px)", borderTop: `0.5px solid ${t.divider}` }}>
          <button onClick={submit} disabled={submitting} style={{
            width: "100%", padding: "14px",
            background: t.accent.red.solid, color: "#fff",
            border: "none", borderRadius: 12,
            fontFamily: F, fontWeight: 700, fontSize: 15,
            cursor: submitting ? "default" : "pointer",
            opacity: submitting ? 0.6 : 1,
          }}>{submitting ? "Schließe ab…" : "Abschließen"}</button>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketSessionDetail — Detail-Ansicht vergangener Markt-Tag
// ─────────────────────────────────────────────────────────────
function MarketSessionDetail({ store, authToken, dark, t, sessionId, items, showToast, onBack, onSessionChanged }) {
  const [detail, setDetail] = useStateM(null);
  const [stornoSale, setStornoSale] = useStateM(null);
  const [confirmStorno, setConfirmStorno] = useStateM(null);
  const [loading, setLoading] = useStateM(true);
  const [error, setError] = useStateM(null);
  const apiBase = `/api/stores/${store.id}`;

  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const d = await apiCall(authToken, `${apiBase}/sessions/${sessionId}`);
      setDetail(d);
    } catch (e) {
      setError(e.message || "Lade-Fehler");
    } finally {
      setLoading(false);
    }
  };
  useEffectM(() => { reload(); /* eslint-disable-next-line */ }, [sessionId]);

  const stornoSubmit = async (sale) => {
    try {
      const updated = await apiCall(authToken, `${apiBase}/sales/${sale.id}`, { method: "PATCH", body: { is_voided: true } });
      setDetail(prev => ({
        ...prev,
        sales: prev.sales.map(s => s.id === sale.id ? { ...s, ...updated, sale_items: s.sale_items } : s),
      }));
      haptic(15);
      showToast("Verkauf storniert");
      onSessionChanged && onSessionChanged();
    } catch (e) {
      showToast(e.message || "Storno fehlgeschlagen", "error");
    }
  };

  const bg = dark ? "#0d1117" : "#dde8f8";

  if (error) {
    return (
      <div style={{ height: "100dvh", maxHeight: "100dvh", background: bg, fontFamily: F, color: t.text, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, overflow: "hidden" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: t.accent.red.solid, fontSize: 14, marginBottom: 12 }}>{error}</div>
          <button onClick={onBack} style={{ background: t.accent.blue.solid, color: "#fff", border: "none", borderRadius: 10, padding: "8px 16px", cursor: "pointer", fontFamily: F, fontSize: 13, fontWeight: 600 }}>Zurück</button>
        </div>
      </div>
    );
  }
  if (loading || !detail) {
    return <div style={{ height: "100dvh", maxHeight: "100dvh", background: bg, fontFamily: F, display: "flex", alignItems: "center", justifyContent: "center", color: t.textMuted, fontSize: 14, overflow: "hidden" }}>Lade…</div>;
  }

  const sales = detail.sales || [];
  const stats = computeMarketStats(sales);
  const sortedSales = [...sales].sort((a, b) => (b.sold_at || "").localeCompare(a.sold_at || ""));

  return (
    <div style={{
      height: "100dvh", maxHeight: "100dvh",
      background: bg, fontFamily: F, color: t.text,
      display: "flex", flexDirection: "column", overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        paddingBottom: 12, paddingLeft: 16, paddingRight: 16,
        background: dark ? "rgba(13,17,23,0.85)" : "rgba(221,232,248,0.85)",
        backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
        borderBottom: `0.5px solid ${t.glassBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onBack} aria-label="Zurück" style={{
            width: 30, height: 30, borderRadius: 14, flexShrink: 0,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
            border: "none", color: t.textMuted, cursor: "pointer", padding: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: t.text, letterSpacing: "-0.3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {detail.location || "Standverkauf"}
            </div>
            <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>
              {fmtDateM(detail.starts_at)} · {fmtDurationM(detail.starts_at, detail.ends_at)}
              {!detail.ends_at && " · LIVE"}
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 16px calc(env(safe-area-inset-bottom, 16px) + 16px)" }}>
        {/* Hero-Stats */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 36, fontWeight: 700, color: t.text, letterSpacing: "-1px", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(stats.total)}</div>
          <div style={{ fontSize: 13, color: t.textSub, marginTop: 4 }}>
            {stats.count} {stats.count === 1 ? "Verkauf" : "Verkäufe"}
            {stats.bundleCount > 0 && ` · ${stats.bundleCount} Bundle`}
          </div>
          {(stats.cash > 0 || stats.card > 0) && (
            <div style={{ display: "inline-flex", gap: 12, marginTop: 8, fontSize: 12, color: t.textMuted }}>
              {stats.cash > 0 && <span>Bar {fmtCurrency(stats.cash)}</span>}
              {stats.card > 0 && <span>Karte {fmtCurrency(stats.card)}</span>}
            </div>
          )}
          {stats.totalDiscount > 0 && (
            <div style={{ fontSize: 11, color: t.accent.orange.solid, marginTop: 6, fontWeight: 600 }}>
              −{fmtCurrency(stats.totalDiscount)} Rabatt
            </div>
          )}
        </div>

        {detail.notes && (
          <div style={{ background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", border: `1px solid ${t.glassBorder}`, borderRadius: 12, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: t.textSub, lineHeight: 1.5 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Notiz</div>
            {detail.notes}
          </div>
        )}

        {/* Verkaufs-Liste */}
        {sortedSales.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: t.textMuted, fontSize: 13 }}>Keine Verkäufe an diesem Tag.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {sortedSales.map(sale => {
              const isVoided = sale.is_voided;
              const sis = sale.sale_items || [];
              const totalQty = sis.reduce((s, i) => s + Number(i.quantity || 1), 0);
              const headline = sale.is_bundle
                ? `Bundle (${sis.length} Produkte · ${totalQty} Stk)`
                : sis.length > 0
                  ? (Number(sis[0].quantity || 1) > 1 ? `${sis[0].quantity}× ${sis[0].item_name}` : sis[0].item_name)
                  : "Verkauf";
              return (
                <button key={sale.id} onClick={() => {
                  // Wenn Markt vorbei: Doppel-Bestätigung
                  if (detail.ends_at && !isVoided) {
                    setConfirmStorno(sale);
                  } else {
                    setStornoSale(sale);
                  }
                }} style={{
                  width: "100%",
                  background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.5)",
                  border: `1px solid ${t.glassBorder}`,
                  borderRadius: 12, padding: "10px 12px",
                  cursor: "pointer", fontFamily: F, textAlign: "left",
                  display: "flex", alignItems: "center", gap: 10,
                  opacity: isVoided ? 0.5 : 1,
                  textDecoration: isVoided ? "line-through" : "none",
                }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                    background: isVoided ? t.accent.red.weak : t.accent.green.weak,
                    color: isVoided ? t.accent.red.solid : t.accent.green.solid,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isVoided ? (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 2L10 10M10 2L2 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6.5L5 9.5L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{headline}</div>
                    <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>
                      {fmtTimeM(sale.sold_at)} · {sale.payment === "cash" ? "Bar" : sale.payment === "card" ? "Karte" : "Andere"}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(sale.total)}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {stornoSale && (
        <MarketSaleStornoSheet
          sale={stornoSale} dark={dark} t={t}
          onClose={() => setStornoSale(null)}
          onConfirm={async () => {
            await stornoSubmit(stornoSale);
            setStornoSale(null);
          }}
        />
      )}

      {confirmStorno && (
        <ConfirmSheet
          dark={dark} t={t}
          title="Verkauf nachträglich stornieren?"
          subtitle={`${fmtTimeM(confirmStorno.sold_at)} · ${fmtCurrency(confirmStorno.total)}`}
          warning="Der Markt ist bereits abgeschlossen. Storno wirkt sich auf Statistiken und Lager aus."
          confirmLabel="Stornieren"
          confirmDestructive={true}
          onCancel={() => setConfirmStorno(null)}
          onConfirm={async () => { await stornoSubmit(confirmStorno); setConfirmStorno(null); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketAddNoteSheet — Notiz mid-Session
// ─────────────────────────────────────────────────────────────
function MarketAddNoteSheet({ dark, t, initialNote, onClose, onSave }) {
  const [note, setNote] = useStateM(initialNote || "");
  const [submitting, setSubmitting] = useStateM(false);
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    await onSave(note.trim());
    setSubmitting(false);
  };
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 92,
        background: dark ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.34)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        animation: "fadeIn 0.22s cubic-bezier(0.32, 0.72, 0, 1) both",
      }}/>
      <div onClick={e => e.stopPropagation()} style={modalShellStyle({ vh, kbOpen, offsetTop, F, dark, width: 400, radius: 24, padding: "20px 22px 22px", zIndex: 93 })}>
        <ModalHeader t={t} dark={dark} F={F} title="Notiz" onClose={onClose} marginBottom={14} />
        <div style={{ background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", borderRadius: 12, padding: "10px 14px", marginBottom: 16, border: `1px solid ${t.glassBorder}` }}>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="Wetter, Andrang, Beobachtungen, …"
            rows={4}
            style={{ width: "100%", background: "transparent", border: "none", outline: "none", fontSize: 14, color: t.text, fontFamily: F, resize: "none", minWidth: 0 }}/>
        </div>
        <button onClick={submit} disabled={submitting} style={{
          width: "100%", padding: "13px",
          background: t.accent.blue.solid, color: "#fff",
          border: "none", borderRadius: 12,
          fontFamily: F, fontWeight: 700, fontSize: 14,
          cursor: submitting ? "default" : "pointer",
          opacity: submitting ? 0.6 : 1,
        }}>{submitting ? "Speichere…" : "Speichern"}</button>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// MarketStats — Tab "Statistik" (übergreifend)
// ─────────────────────────────────────────────────────────────
function MarketStats({ sessions, items, authToken, store, dark, t }) {
  // Lade Sales aller Sessions parallel (max die letzten 30 für Performance).
  const [allSales, setAllSales] = useStateM([]);
  const [loading, setLoading] = useStateM(true);

  useEffectM(() => {
    let cancelled = false;
    (async () => {
      try {
        const recent = sessions.slice(0, 30);
        const results = await Promise.all(recent.map(s =>
          apiCall(authToken, `/api/stores/${store.id}/sessions/${s.id}`).catch(() => null)
        ));
        if (cancelled) return;
        const flat = results.filter(Boolean).flatMap(d => (d.sales || []).map(sale => ({ ...sale, _location: d.location })));
        setAllSales(flat);
      } catch (e) {
        // Fehler nicht-fatal — leere Stats
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessions.length, store.id]);

  const finished = sessions.filter(s => s.ends_at);

  // Top-Produkte über alle Sessions
  const topProducts = useMemoM(() => {
    const map = new Map();
    for (const sale of allSales) {
      if (sale.is_voided) continue;
      const sis = sale.sale_items || [];
      let bundleStdSum = 0;
      if (sale.is_bundle) {
        bundleStdSum = sis.reduce((s, i) => s + (Number(i.wish_price) || 0) * Number(i.quantity || 1), 0);
      }
      for (const si of sis) {
        if (!si.item_id) continue;
        const cur = map.get(si.item_id) || { qty: 0, revenue: 0, name: si.item_name };
        cur.qty += Number(si.quantity || 1);
        if (sale.is_bundle) {
          const itemStd = (Number(si.wish_price) || 0) * Number(si.quantity || 1);
          if (bundleStdSum > 0) cur.revenue += (itemStd / bundleStdSum) * Number(sale.total || 0);
        } else {
          const itemStd = (Number(si.unit_price) || 0) * Number(si.quantity || 1);
          const totalStd = sis.reduce((s, x) => s + (Number(x.unit_price) || 0) * Number(x.quantity || 1), 0);
          cur.revenue += totalStd > 0 ? (itemStd / totalStd) * Number(sale.total || 0) : 0;
        }
        cur.name = si.item_name;
        map.set(si.item_id, cur);
      }
    }
    return [...map.entries()].map(([id, v]) => ({ id, ...v })).sort((a, b) => b.qty - a.qty).slice(0, 5);
  }, [allSales]);

  // Top-Orte
  const topLocations = useMemoM(() => {
    const map = new Map();
    for (const sale of allSales) {
      if (sale.is_voided) continue;
      const loc = sale._location || "Ohne Ort";
      const cur = map.get(loc) || { count: 0, revenue: 0 };
      cur.count += 1;
      cur.revenue += Number(sale.total || 0);
      map.set(loc, cur);
    }
    // Sessions pro Ort zählen
    const sessionsByLoc = new Map();
    for (const s of sessions) {
      const loc = s.location || "Ohne Ort";
      sessionsByLoc.set(loc, (sessionsByLoc.get(loc) || 0) + 1);
    }
    return [...map.entries()].map(([loc, v]) => ({
      location: loc, ...v, sessionCount: sessionsByLoc.get(loc) || 0,
    })).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
  }, [allSales, sessions]);

  // Gesamt-Stats
  const totalStats = computeMarketStats(allSales);
  const avgPerSession = finished.length > 0 ? {
    revenue: totalStats.total / finished.length,
    count: totalStats.count / finished.length,
  } : null;

  if (loading) {
    return <div style={{ textAlign: "center", color: t.textMuted, padding: 40, fontSize: 14 }}>Lade Statistik…</div>;
  }
  if (sessions.length === 0) {
    return <div style={{ textAlign: "center", color: t.textMuted, padding: 60, fontSize: 14, lineHeight: 1.5 }}>Noch keine Markt-Tage.<br/>Starte einen um Statistiken zu sehen.</div>;
  }

  const Card = ({ title, children, sub }) => (
    <div style={{
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
      border: `1px solid ${t.glassBorder}`,
      borderRadius: 14, padding: "14px 16px",
      marginBottom: 10,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: sub ? 2 : 6 }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: t.textSub, marginBottom: 8 }}>{sub}</div>}
      {children}
    </div>
  );

  return (
    <div>
      {/* Hero */}
      <div style={{
        background: dark ? "rgba(48,209,88,0.08)" : "rgba(48,209,88,0.06)",
        border: `1px solid ${t.accent.green.solid}30`,
        borderRadius: 14, padding: "18px 20px", marginBottom: 12,
        textAlign: "center",
      }}>
        <div style={{ fontSize: 32, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{fmtCurrency(totalStats.total)}</div>
        <div style={{ fontSize: 12, color: t.textSub, marginTop: 4 }}>
          {finished.length} Markt-{finished.length === 1 ? "Tag" : "Tage"} · {totalStats.count} {totalStats.count === 1 ? "Verkauf" : "Verkäufe"}
        </div>
      </div>

      {avgPerSession && finished.length >= 2 && (
        <Card title="Durchschnitt pro Markt-Tag">
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: t.textSub }}>⌀ Umsatz</span>
            <span style={{ color: t.text, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{fmtCurrency(avgPerSession.revenue)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 4 }}>
            <span style={{ color: t.textSub }}>⌀ Verkäufe</span>
            <span style={{ color: t.text, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{Math.round(avgPerSession.count * 10) / 10}</span>
          </div>
        </Card>
      )}

      {topProducts.length > 0 && (
        <Card title="Top-Produkte" sub="Nach Stückzahl, alle Markt-Tage">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topProducts.map((p, i) => (
              <div key={p.id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 0",
                borderTop: i > 0 ? `0.5px solid ${t.divider}` : "none",
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  background: i === 0 ? t.accent.orange.weak : t.accent.green.weak,
                  color: i === 0 ? t.accent.orange.solid : t.accent.green.solid,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 700,
                }}>{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                <div style={{ fontSize: 12, color: t.textSub, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
                  {p.qty}× <span style={{ color: t.text, fontWeight: 600 }}>{fmtCurrency(p.revenue)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {topLocations.length > 0 && (
        <Card title="Top-Orte" sub="Nach Umsatz">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {topLocations.map((l, i) => (
              <div key={l.location} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 0",
                borderTop: i > 0 ? `0.5px solid ${t.divider}` : "none",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.location}</div>
                  <div style={{ fontSize: 11, color: t.textSub, marginTop: 1 }}>{l.sessionCount} {l.sessionCount === 1 ? "Markt-Tag" : "Markt-Tage"}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: t.text, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{fmtCurrency(l.revenue)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {totalStats.totalDiscount > 0 && (
        <Card title="Rabatte">
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <span style={{ color: t.textSub }}>Insgesamt gegeben</span>
            <span style={{ color: t.accent.orange.solid, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>−{fmtCurrency(totalStats.totalDiscount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginTop: 4 }}>
            <span style={{ color: t.textSub }}>Bei {totalStats.discountedSales} {totalStats.discountedSales === 1 ? "Verkauf" : "Verkäufen"}</span>
            <span style={{ color: t.text, fontWeight: 600 }}>⌀ {totalStats.avgDiscountPct}%</span>
          </div>
        </Card>
      )}
    </div>
  );
}

export { MarketHub };
